import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Table, DatePicker, Select, Badge, Button, Tag, Modal, Card, Alert, 
  Drawer, Input, Form, Row, Col, Statistic, Tooltip, Divider, Icon, Collapse, Spin, Popover
} from 'antd';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import {
  initDB,
  getAnalysisDataForComponent,
  saveAssetAnalysis,
} from './utils/analysisDB';
import moment from 'moment';

// Apply animated theme
am4core.useTheme(am4themes_animated);
import {
  loadCSV,
  parseStandardRate,
  determineConsumptionRateUnit,
  calculateConsumptionVariance,
  calculateIdlingPercentage,
  formatRunningHours,
  formatMovingTime,
  determineIssues,
  determinePriority
} from './utils/csvLoader';

const { Panel } = Collapse;

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

// Helper functions
const calculateVehicleAge = (purchaseDate) => {
  if (!purchaseDate) return 'N/A';
  const purchase = moment(purchaseDate);
  const now = moment();
  const years = now.diff(purchase, 'years');
  const months = now.diff(purchase.clone().add(years, 'years'), 'months');
  return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
};

// Helper function to get consumption rate unit display
const getConsumptionRateUnit = (asset) => {
  return asset.consumptionRateUnit || 'KMS/LTR'; // Default to KMS/LTR
};

// Helper function to format consumption rate with unit
const formatConsumptionRate = (value, unit) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(2)} ${unit}`;
};

const getPriorityBadge = (asset) => {
  if (asset.priority === 'critical') return <Badge status="error" text="Critical" />;
  if (asset.priority === 'high') return <Badge status="error" text="High Priority" />;
  if (asset.priority === 'low') return <Badge status="success" text="Low Priority" />;
  return <Badge status="default" text="Normal" />;
};

const getIssueTags = (asset) => {
  const issueMap = {
    'highConsumptionVariance': { text: 'High Consumption Variance', color: 'red' },
    'highRefuelDifference': { text: 'High Refuel Difference', color: 'red' },
    'highIdling': { text: 'High Idling', color: 'red' },
    'possibleDrain': { text: 'Possible Drain', color: 'red' },
    'dataGap24Plus': { text: 'Data Gap 24+ Hours', color: 'red' },
    'lowDataCompleteness': { text: 'Low Data Completeness', color: 'red' },
    'greenConsumptionRate': { text: 'Abnormal Positive Rate', color: 'green' }
  };
  return asset.issues.map(issue => {
    const issueInfo = issueMap[issue] || { text: issue, color: 'default' };
    return <Tag key={issue} color={issueInfo.color}>{issueInfo.text}</Tag>;
  });
};

// Get issue tags with alert status indicators
const getIssueTagsWithStatus = (asset, analysisData) => {
  const issueMap = {
    'highConsumptionVariance': { text: 'High Consumption Variance', color: 'red' },
    'highRefuelDifference': { text: 'High Refuel Difference', color: 'red' },
    'highIdling': { text: 'High Idling', color: 'red' },
    'possibleDrain': { text: 'Possible Drain', color: 'red' },
    'dataGap24Plus': { text: 'Data Gap 24+ Hours', color: 'red' },
    'lowDataCompleteness': { text: 'Low Data Completeness', color: 'red' },
    'greenConsumptionRate': { text: 'Abnormal Positive Rate', color: 'green' }
  };
  
  const assetAnalysis = analysisData?.[asset.id] || {};
  
  return asset.issues.map(issue => {
    const issueInfo = issueMap[issue] || { text: issue, color: 'default' };
    const issueData = assetAnalysis[issue];
    
    // Check if analysis is saved (required for alert status)
    const hasAnalysis = issueData?.analysis && issueData.analysis.trim() !== '';
    const hasSuggestedAction = issueData?.suggestedAction && issueData.suggestedAction.trim() !== '';
    const alertStatus = issueData?.alertStatus;
    
    // Only show status if analysis is complete
    let statusIcon = null;
    if (hasAnalysis && hasSuggestedAction) {
      if (alertStatus === 'confirmed') {
        statusIcon = <span style={{ marginLeft: 4, color: '#52c41a', fontWeight: 'bold' }}>✓</span>;
      } else if (alertStatus === 'discarded') {
        statusIcon = <span style={{ marginLeft: 4, color: '#ff4d4f', fontWeight: 'bold' }}>✗</span>;
      } else {
        // Analysis complete but no status set
        statusIcon = <span style={{ marginLeft: 4, color: '#999', fontSize: '10px' }}>○</span>;
      }
    }
    
    return (
      <Tag key={issue} color={issueInfo.color} style={{ marginBottom: 4 }}>
        {issueInfo.text}
        {statusIcon}
      </Tag>
    );
  });
};

// Helper function to get alert status for a single issue
const getAlertStatus = (issueData, hasAnalysisStartTime) => {
  if (!hasAnalysisStartTime && !issueData?.analysis) {
    return 'not_analysed';
  }
  if (hasAnalysisStartTime && !issueData?.analysis) {
    return 'under_analysis';
  }
  // Check that finalAction exists and is not empty
  const hasActionTaken = issueData?.finalAction && issueData.finalAction.trim() !== '';
  if (issueData?.analysis && issueData?.suggestedAction && !hasActionTaken) {
    return 'analysed';
  }
  if (issueData?.analysis && issueData?.suggestedAction && hasActionTaken) {
    if (issueData?.alertStatus === 'confirmed') {
      return 'analysed_action_taken_confirmed';
    }
    if (issueData?.alertStatus === 'discarded') {
      return 'analysed_action_taken_discarded';
    }
    return 'analysed_action_taken';
  }
  return 'not_analysed';
};

// Helper function to get analysis status counts for an asset
const getAnalysisStatusCounts = (asset, analysisData) => {
  if (!asset || !asset.issues) return {};
  
  const assetAnalysis = analysisData?.[asset.id] || {};
  const counts = {
    not_analysed: 0,
    under_analysis: 0,
    analysed: 0,
    analysed_action_taken: 0
  };
  
  asset.issues.forEach(issue => {
    const issueData = assetAnalysis[issue];
    const hasAnalysisStartTime = issueData?.analysisStartTime;
    // Check that finalAction exists and is not empty
    const hasActionTaken = issueData?.finalAction && issueData.finalAction.trim() !== '';
    
    // Determine analysis status (not alert status)
    if (!hasAnalysisStartTime && !issueData?.analysis) {
      counts.not_analysed++;
    } else if (hasAnalysisStartTime && !issueData?.analysis) {
      counts.under_analysis++;
    } else if (issueData?.analysis && issueData?.suggestedAction && !hasActionTaken) {
      counts.analysed++;
    } else if (issueData?.analysis && issueData?.suggestedAction && hasActionTaken) {
      counts.analysed_action_taken++;
    } else {
      counts.not_analysed++;
    }
  });
  
  return counts;
};

// Helper function to get alert status counts for an asset
// Alert status can be set once analysis is saved (doesn't require action taken)
const getAlertStatusCounts = (asset, analysisData) => {
  if (!asset || !asset.issues) return { confirmed: 0, discarded: 0, no_status: 0 };
  
  const assetAnalysis = analysisData?.[asset.id] || {};
  const counts = {
    confirmed: 0,
    discarded: 0,
    no_status: 0
  };
  
  asset.issues.forEach(issue => {
    const issueData = assetAnalysis[issue];
    
    // Alert status can be set once analysis and suggestedAction are saved
    const hasAnalysis = issueData?.analysis && issueData.analysis.trim() !== '';
    const hasSuggestedAction = issueData?.suggestedAction && issueData.suggestedAction.trim() !== '';
    
    // Count alert status if analysis is complete (analysis + suggestedAction exist)
    if (hasAnalysis && hasSuggestedAction) {
      if (issueData.alertStatus === 'confirmed') {
        counts.confirmed++;
      } else if (issueData.alertStatus === 'discarded') {
        counts.discarded++;
      } else {
        // If analysis is complete but no status is set, count as no_status
        counts.no_status++;
      }
    }
  });
  
  return counts;
};

const IoTDailyReportAnalysis = () => {
  const [dateRange, setDateRange] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [alertStatusFilter, setAlertStatusFilter] = useState('all');
  const [analysisStatusFilter, setAnalysisStatusFilter] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [analysisDrawerVisible, setAnalysisDrawerVisible] = useState(false);
  // Initialize database and load analysisData on mount
  const [analysisData, setAnalysisData] = useState(() => {
    try {
      initDB(); // Initialize database
      return getAnalysisDataForComponent(); // Load data in component format
    } catch (e) {
      console.error('Error loading analysis data from database:', e);
      return {};
    }
  });
  const [analysisStarted, setAnalysisStarted] = useState({}); // Track when analysis started for each asset

  // Save analysisData to database whenever it changes
  useEffect(() => {
    try {
      // The database is saved automatically when saveAssetAnalysis is called
      // This effect just ensures the component state is in sync
    } catch (e) {
      console.error('Error syncing analysis data:', e);
    }
  }, [analysisData]);

  // Helper function to prepare analysis data for database submission
  // This function formats the data structure for API submission
  const prepareAnalysisDataForDatabase = (assetId, issueKey, issueData) => {
    return {
      assetId: assetId,
      issueKey: issueKey,
      issueId: issueData.issueId,
      analysis: issueData.analysis || null,
      suggestedAction: issueData.suggestedAction || null,
      finalAction: issueData.finalAction || null,
      alertStatus: issueData.alertStatus || null, // 'confirmed', 'discarded', or null
      analyzedBy: issueData.analyzedBy || null,
      analyzedAt: issueData.analyzedAt || null,
      analysisStartTime: issueData.analysisStartTime || null,
      analysisEndTime: issueData.analysisEndTime || null,
      analysisTimeTaken: issueData.analysisTimeTaken ? issueData.analysisTimeTaken.totalSeconds : null,
      actionTakenUpdatedBy: issueData.actionTakenUpdatedBy || null,
      actionTakenUpdatedAt: issueData.actionTakenUpdatedAt || null,
      alertStatusUpdatedBy: issueData.alertStatusUpdatedBy || null,
      alertStatusUpdatedAt: issueData.alertStatusUpdatedAt || null,
      status: issueData.status || 'pending',
      sentToClientAt: issueData.sentToClientAt || null,
      sentToClientBy: issueData.sentToClientBy || null,
      clientMessage: issueData.clientMessage || null,
      responseTime: issueData.responseTime ? issueData.responseTime.totalSeconds : null,
      clientResponseTime: issueData.clientResponseTime ? issueData.clientResponseTime.totalSeconds : null,
      closedAt: issueData.closedAt || null,
      closedBy: issueData.closedBy || null,
      updatedAt: issueData.updatedAt || null
    };
  };

  // Example function to save to database (to be implemented)
  // Uncomment and modify when database API is ready
  // const saveAnalysisDataToDatabase = async (assetAnalysisData, assetId) => {
  //   try {
  //     const issues = Object.keys(assetAnalysisData);
  //     const payload = issues.map(issueKey => 
  //       prepareAnalysisDataForDatabase(assetId, issueKey, assetAnalysisData[issueKey])
  //     );
  //     
  //     const response = await fetch('/api/analysis', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ assetId, issues: payload })
  //     });
  //     
  //     if (!response.ok) {
  //       throw new Error('Failed to save analysis data');
  //     }
  //     
  //     return await response.json();
  //   } catch (error) {
  //     console.error('Error saving to database:', error);
  //     throw error;
  //   }
  // };

  const [iotReports, setIotReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [standardRates, setStandardRates] = useState({}); // Map of model name to standard rates

  // Helper function to parse time string to decimal hours
  const parseTimeToHours = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      return parseFloat(parts[0]) + parseFloat(parts[1]) / 60;
    } else if (parts.length === 3) {
      return parseFloat(parts[0]) + parseFloat(parts[1]) / 60 + parseFloat(parts[2]) / 3600;
    }
    return 0;
  };

  // Helper function to format hours to HH:MM:SS
  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = Math.floor(((hours - h) * 60 - m) * 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Load and process CSV data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load both CSV files
        const [iotData, modelData] = await Promise.all([
          loadCSV('/data/iot-device-report.csv'),
          loadCSV('/data/asset-model-list.csv')
        ]);

        // Process standard rates from model list
        const ratesMap = {};
        modelData.forEach(row => {
          const modelName = row.Name?.trim();
          if (modelName) {
            const rate = parseStandardRate(row['Standard Fuel Consumption Rate']);
            if (rate) {
              ratesMap[modelName] = rate;
            }
          }
        });
        setStandardRates(ratesMap);

        // Process IoT data - aggregate by asset and date
        const assetMap = new Map();
        
        iotData.forEach((row, index) => {
          const assetCode = row['Asset Code']?.trim();
          const altName = row['Alt Name']?.trim();
          const assetName = altName && altName !== 'NA' ? `${assetCode} ${altName}` : assetCode;
          
          if (!assetCode) return;

          const date = row.Date?.trim();
          const model = row['Asset Model']?.trim();
          const location = row.Location?.trim();
          const subLocation = row['Sub Location']?.trim();
          const assetType = row['Asset Type']?.trim();
          const assetSubType = row['Asset Sub Type']?.trim();

          // Parse numeric values
          const dataAvailable = parseFloat(row['Data Available (hours)']) || 0;
          const fuelConsumed = parseFloat(row['Fuel Consumed (ltrs)']) || 0;
          const openingFuelLevel = parseFloat(row['Sensor Starting Fuel (ltrs)']) || null;
          const closingFuelLevel = parseFloat(row['Sensor Ending Fuel (ltrs)']) || null;
          const distanceTravelled = parseFloat(row['Distance Travelled (km)']) || 0;
          const idleTimeMin = parseFloat(row['Idle Time (min)']) || 0;
          const ignitionTimeMin = parseFloat(row['Ignition Time (min)']) || 0;
          const refuelDetected = parseFloat(row['Possible Refuel']) || 0;
          const refuelEntered = parseFloat(row['Manual Refuel Quantity']) || 0;
          const possibleDrain = parseFloat(row['Possible Drain']) || 0;

          // Get consumption rates from CSV
          const consumptionRateKms = parseFloat(row['Consumption Rate (KMS/Ltr)']) || 0;
          const consumptionRateLtr = parseFloat(row['Consumption Rate (Ltr/Hours)']) || 0;

          // Get standard rate for this model
          const stdRateInfo = ratesMap[model] || {};
          const unit = determineConsumptionRateUnit(assetType, distanceTravelled, !!stdRateInfo.kmsPerLtr, !!stdRateInfo.ltrPerHour);
          
          let stdRate = null;
          let actualRate = null;
          
          if (unit === 'KMS/LTR') {
            stdRate = stdRateInfo.kmsPerLtr;
            actualRate = consumptionRateKms > 0 ? consumptionRateKms : (distanceTravelled > 0 && fuelConsumed > 0 ? distanceTravelled / fuelConsumed : null);
          } else if (unit === 'LTR/HOURS') {
            stdRate = stdRateInfo.ltrPerHour;
            const runningHours = ignitionTimeMin / 60;
            actualRate = consumptionRateLtr > 0 ? consumptionRateLtr : (runningHours > 0 && fuelConsumed > 0 ? fuelConsumed / runningHours : null);
          }

          // Calculate consumption rate based on refuel entered (assuming manual entry is correct)
          // Adjust fuel consumed by the difference between detected and entered refuel
          const refuelDifference = refuelDetected - refuelEntered;
          const adjustedFuelConsumed = fuelConsumed - refuelDifference; // If entered > detected, fuel consumed increases
          let consumptionRateBasedOnRefuelEntered = null;
          
          if (unit === 'KMS/LTR') {
            consumptionRateBasedOnRefuelEntered = distanceTravelled > 0 && adjustedFuelConsumed > 0 ? distanceTravelled / adjustedFuelConsumed : null;
          } else if (unit === 'LTR/HOURS') {
            const runningHours = ignitionTimeMin / 60;
            consumptionRateBasedOnRefuelEntered = runningHours > 0 && adjustedFuelConsumed > 0 ? adjustedFuelConsumed / runningHours : null;
          }

          // Calculate consumption variance
          const consumptionVariance = actualRate && stdRate ? calculateConsumptionVariance(actualRate, stdRate, unit) : null;

          // Calculate idling percentage
          const idlingPercentage = calculateIdlingPercentage(idleTimeMin, ignitionTimeMin);

          // Format running hours
          const runningHours = formatRunningHours(ignitionTimeMin);

          // Calculate moving time (ignition time - idle time)
          const movingTimeMin = ignitionTimeMin - idleTimeMin;
          const movingTime = formatMovingTime(movingTimeMin);

          // Calculate average speed (KM/H) = Distance / Moving Time (hours)
          const averageSpeed = movingTimeMin > 0 && distanceTravelled > 0 
            ? (distanceTravelled * 60) / movingTimeMin 
            : null;

          // Calculate consumption per hour based on moving time (excluding idle time)
          const consumptionPerHourMovingTime = movingTimeMin > 0 && fuelConsumed > 0
            ? (fuelConsumed * 60) / movingTimeMin
            : null;

          // Calculate consumption per hour based on moving time using adjusted fuel consumed (refuel entered)
          const consumptionPerHourMovingTimeBasedOnRefuelEntered = movingTimeMin > 0 && adjustedFuelConsumed > 0
            ? (adjustedFuelConsumed * 60) / movingTimeMin
            : null;

          // Calculate data completeness
          const expectedDataHours = 24.0; // Assuming daily reports
          const actualDataHours = dataAvailable;
          const dataCompleteness = expectedDataHours > 0 ? (actualDataHours / expectedDataHours) * 100 : 0;
          const dataGaps = expectedDataHours - actualDataHours;
          const hasDataGap24Plus = dataGaps >= 24;

          // Create asset key
          const assetKey = `${assetCode}-${location}`;
          const currentDate = date ? moment(date, 'DD-MM-YYYY') : moment();
          
          if (!assetMap.has(assetKey)) {
            assetMap.set(assetKey, {
              id: assetMap.size + 1,
              asset: assetName,
              assetCode: assetCode,
              model: model || 'N/A',
              location: location || 'N/A',
              subLocation: subLocation || 'N/A',
              assetType: assetType || 'N/A',
              assetSubType: assetSubType || 'N/A',
              purchaseDate: null, // Not available in CSV, can be added later
              dataUpto: currentDate.format('YYYY-MM-DD 23:59:59'),
              dataFrom: currentDate.format('YYYY-MM-DD 00:00:00'),
              expectedDataHours: expectedDataHours,
              actualDataHours: actualDataHours,
              dataCompleteness: dataCompleteness,
              dataGaps: dataGaps,
              hasDataGap24Plus: hasDataGap24Plus,
              runningHours: runningHours,
              movingTime: movingTime,
              averageSpeed: averageSpeed,
              distanceTravelled: distanceTravelled,
              fuelConsumed: fuelConsumed,
              openingFuelLevel: openingFuelLevel,
              closingFuelLevel: closingFuelLevel,
              refuelDetected: refuelDetected,
              refuelEntered: refuelEntered,
              refuelDifference: refuelDetected - refuelEntered,
              possibleDrain: possibleDrain,
              consumptionRateUnit: unit,
              stdConsumptionRate: stdRate,
              consumptionRate: actualRate,
              consumptionRateBasedOnRefuelEntered: consumptionRateBasedOnRefuelEntered,
              consumptionVariance: consumptionVariance,
              consumptionPerHour: consumptionRateLtr || (ignitionTimeMin > 0 ? (fuelConsumed / (ignitionTimeMin / 60)) : null),
              consumptionPerHourMovingTime: consumptionPerHourMovingTime,
              consumptionPerHourMovingTimeBasedOnRefuelEntered: consumptionPerHourMovingTimeBasedOnRefuelEntered,
              idlingHours: formatMovingTime(idleTimeMin), // Format as HH:MM
              idlingHoursDecimal: idleTimeMin / 60, // Keep decimal for calculations if needed
              idlingPercentage: idlingPercentage,
              issues: [],
              priority: 'normal',
              csvDate: currentDate // Store date for comparison
            });
          } else {
            // Keep only the latest date's data for each asset
            const existing = assetMap.get(assetKey);
            const existingDate = moment(existing.csvDate || existing.dataUpto);
            
            if (currentDate.isAfter(existingDate)) {
              // Replace with newer data
              assetMap.set(assetKey, {
                id: existing.id,
                asset: assetName,
                assetCode: assetCode,
                model: model || 'N/A',
                location: location || 'N/A',
                subLocation: subLocation || 'N/A',
                assetType: assetType || 'N/A',
                assetSubType: assetSubType || 'N/A',
                purchaseDate: existing.purchaseDate,
                dataUpto: currentDate.format('YYYY-MM-DD 23:59:59'),
                dataFrom: currentDate.format('YYYY-MM-DD 00:00:00'),
                expectedDataHours: expectedDataHours,
                actualDataHours: actualDataHours,
                dataCompleteness: dataCompleteness,
                dataGaps: dataGaps,
                hasDataGap24Plus: hasDataGap24Plus,
                runningHours: runningHours,
                movingTime: movingTime,
                averageSpeed: averageSpeed,
                distanceTravelled: distanceTravelled,
                fuelConsumed: fuelConsumed,
                openingFuelLevel: openingFuelLevel,
                closingFuelLevel: closingFuelLevel,
                refuelDetected: refuelDetected,
                refuelEntered: refuelEntered,
                refuelDifference: refuelDetected - refuelEntered,
                possibleDrain: possibleDrain,
                consumptionRateUnit: unit,
                stdConsumptionRate: stdRate,
                consumptionRate: actualRate,
                consumptionRateBasedOnRefuelEntered: consumptionRateBasedOnRefuelEntered,
                consumptionVariance: consumptionVariance,
                consumptionPerHour: consumptionRateLtr || (ignitionTimeMin > 0 ? (fuelConsumed / (ignitionTimeMin / 60)) : null),
                consumptionPerHourMovingTime: consumptionPerHourMovingTime,
                consumptionPerHourMovingTimeBasedOnRefuelEntered: consumptionPerHourMovingTimeBasedOnRefuelEntered,
                idlingHours: formatMovingTime(idleTimeMin), // Format as HH:MM
                idlingHoursDecimal: idleTimeMin / 60, // Keep decimal for calculations if needed
                idlingPercentage: idlingPercentage,
                issues: [],
                priority: 'normal',
                csvDate: currentDate
              });
            }
          }
        });

        // Calculate issues and priority for each asset
        const processedReports = Array.from(assetMap.values()).map(asset => {
          const issues = determineIssues(asset, asset.consumptionRateUnit, asset.stdConsumptionRate, asset.consumptionRate);
          const priority = determinePriority(issues);
          return {
            ...asset,
            issues,
            priority
          };
        });

        setIotReports(processedReports);
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Helper function to determine if value should be red
  const isRed = (value, type, asset = null) => {
    if (type === 'consumptionVariance') {
      if (!asset) return value < -10; // Default fallback
      const unit = getConsumptionRateUnit(asset);
      // For KMS/LTR and KWH/L: Higher rate = better (lower fuel consumption)
      //   Negative variance = bad (worse fuel efficiency)
      // For LTR/HOURS: Lower rate = better (lower fuel consumption)
      //   Positive variance = bad (higher fuel consumption)
      if (unit === 'KMS/LTR' || unit === 'KWH/L') {
        return value < -10; // Negative variance is bad (worse fuel efficiency)
      } else if (unit === 'LTR/HOURS') {
        return value > 10; // Positive variance is bad (higher fuel consumption)
      }
      return value < -10; // Default fallback
    }
    if (type === 'refuelDifference') return Math.abs(value) > 5; // Both positive and negative differences > 5L should be red
    if (type === 'idlingPercentage') return value > 5; // More than 5% idling
    if (type === 'possibleDrain') return value > 0;
    return false;
  };

  // Helper function to determine if value should be green (abnormal but positive - better fuel efficiency)
  const isGreen = (value, type, asset = null) => {
    if (type === 'consumptionVariance') {
      if (!asset) return value > 10; // Default fallback
      const unit = getConsumptionRateUnit(asset);
      // For KMS/LTR and KWH/L: Higher rate = better (lower fuel consumption)
      //   Positive variance > 10% = good (better fuel efficiency)
      // For LTR/HOURS: Lower rate = better (lower fuel consumption)
      //   Negative variance < -10% = good (lower fuel consumption)
      if (unit === 'KMS/LTR' || unit === 'KWH/L') {
        return value > 10; // Positive variance is good (better fuel efficiency)
      } else if (unit === 'LTR/HOURS') {
        return value < -10; // Negative variance is good (lower fuel consumption)
      }
      return value > 10; // Default fallback
    }
    return false;
  };

  const getCellStyle = (value, type, asset = null) => {
    if (isRed(value, type, asset)) return { backgroundColor: '#fff1f0', color: '#cf1322', fontWeight: 'bold' };
    if (isGreen(value, type, asset)) return { backgroundColor: '#f6ffed', color: '#389e0d', fontWeight: 'bold' };
    return {};
  };


  const priorityOrderContent = (
    <div>
      <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Analysis Priority Order:</div>
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        <li>Red consumption rate with high variance (&lt; -10%)</li>
        <li>Red refuel difference (Absolute difference &gt; 5L between detected and entered)</li>
        <li>Red idling percentage (&gt; 5%)</li>
        <li>Red data gaps (Missing data for 24+ hours)</li>
        <li>Green consumption rates (abnormal positive variance &gt; 15%)</li>
      </ol>
    </div>
  );

  const columns = [
    {
      title: (
        <span>
          Priority{' '}
          <Popover content={priorityOrderContent} title="Analysis Priority Order" trigger="click">
            <Icon type="info-circle" style={{ color: '#1890ff', cursor: 'pointer', marginLeft: 4 }} />
          </Popover>
        </span>
      ),
      width: 120,
      fixed: 'left',
      render: (text, record) => getPriorityBadge(record),
      sorter: (a, b) => {
        const priorityOrder = { 'critical': 0, 'high': 1, 'low': 2, 'normal': 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      },
      defaultSortOrder: 'ascend'
    },
    {
      title: 'Asset',
      dataIndex: 'asset',
      width: 200,
      fixed: 'left',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.model}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.location}</div>
        </div>
      )
    },
    {
      title: (
        <span>
          Issues & Alert Status{' '}
          <Tooltip title={
            <div>
              <div>✓ = Confirmed</div>
              <div>✗ = Discarded</div>
              <div>○ = No Status (Analysis complete but status not set)</div>
            </div>
          }>
            <Icon type="info-circle" style={{ color: '#1890ff', cursor: 'pointer', marginLeft: 4 }} />
          </Tooltip>
        </span>
      ),
      width: 350,
      render: (text, record) => {
        const issuesWithStatus = getIssueTagsWithStatus(record, analysisData);
        const counts = getAlertStatusCounts(record, analysisData);
        const totalWithStatus = counts.confirmed + counts.discarded + counts.no_status;
        
        return (
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {issuesWithStatus}
            </div>
            {record.issues && record.issues.length > 0 && (
              <div style={{ fontSize: '11px', color: '#666', borderTop: '1px solid #f0f0f0', paddingTop: 4 }}>
                <span style={{ color: '#52c41a' }}>✓ Confirmed: {counts.confirmed}</span>
                {' | '}
                <span style={{ color: '#ff4d4f' }}>✗ Discarded: {counts.discarded}</span>
                {' | '}
                <span style={{ color: '#999' }}>○ No Status: {counts.no_status}</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Analysis Status',
      width: 300,
      render: (text, record) => {
        const counts = getAnalysisStatusCounts(record, analysisData);
        
        return (
          <div style={{ fontSize: '12px' }}>
            {counts.not_analysed > 0 && (
              <div style={{ marginBottom: 4 }}>
                <Badge status="default" text={`Not Analysed: ${counts.not_analysed}`} />
              </div>
            )}
            {counts.under_analysis > 0 && (
              <div style={{ marginBottom: 4 }}>
                <Badge status="processing" text={`Under Analysis: ${counts.under_analysis}`} />
              </div>
            )}
            {counts.analysed > 0 && (
              <div style={{ marginBottom: 4 }}>
                <Badge status="success" text={`Analysed: ${counts.analysed}`} />
              </div>
            )}
            {counts.analysed_action_taken > 0 && (
              <div style={{ marginBottom: 4 }}>
                <Badge status="warning" text={`Action Taken: ${counts.analysed_action_taken}`} />
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Data Upto',
      dataIndex: 'dataUpto',
      width: 160,
      render: (text) => moment(text).format('DD-MM-YYYY HH:mm')
    },
    {
      title: 'Data Completeness',
      width: 180,
      render: (text, record) => {
        const completeness = record.dataCompleteness || 100;
        const hasGap = record.hasDataGap24Plus;
        const gapHours = record.dataGaps || 0;
        const isLowCompleteness = completeness < 95;
        
        return (
          <div>
            <div style={(hasGap || isLowCompleteness) ? { color: '#cf1322', fontWeight: 'bold' } : {}}>
              {completeness.toFixed(1)}%
            </div>
            {hasGap && (
              <div style={{ fontSize: '11px', color: '#cf1322' }}>
                Gap: {gapHours.toFixed(1)}h
              </div>
            )}
            {hasGap && (
              <Tag color="red" style={{ marginTop: 4 }}>24+ Hours Missing</Tag>
            )}
            {isLowCompleteness && !hasGap && (
              <Tag color="red" style={{ marginTop: 4 }}>Low Completeness</Tag>
            )}
          </div>
        );
      },
      sorter: (a, b) => (a.dataCompleteness || 100) - (b.dataCompleteness || 100)
    },
    {
      title: 'Running Hours',
      dataIndex: 'runningHours',
      width: 130,
      align: 'right'
    },
    {
      title: 'Moving Time',
      dataIndex: 'movingTime',
      width: 120,
      align: 'right',
      render: (val) => val || '-'
    },
    {
      title: 'Idling Hours',
      dataIndex: 'idlingHours',
      width: 120,
      align: 'right',
      render: (val) => val || '-'
    },
    {
      title: 'Idling %',
      dataIndex: 'idlingPercentage',
      width: 100,
      align: 'right',
      render: (val, record) => {
        if (val == null) return '-';
        return (
          <span style={getCellStyle(val, 'idlingPercentage')}>
            {val.toFixed(1)}%
          </span>
        );
      },
      sorter: (a, b) => (a.idlingPercentage || 0) - (b.idlingPercentage || 0)
    },
    {
      title: 'Average Speed (KM/H)',
      dataIndex: 'averageSpeed',
      width: 150,
      align: 'right',
      render: (val) => val != null ? val.toFixed(2) : '-'
    },
    {
      title: 'Distance (kms)',
      dataIndex: 'distanceTravelled',
      width: 120,
      align: 'right',
      render: (val) => val != null ? val.toFixed(2) : '-'
    },
    {
      title: 'Fuel Consumed (L)',
      dataIndex: 'fuelConsumed',
      width: 140,
      align: 'right',
      render: (val) => val != null ? val.toFixed(2) : '-'
    },
    {
      title: 'Opening Fuel Level (L)',
      dataIndex: 'openingFuelLevel',
      width: 160,
      align: 'right',
      render: (val) => val != null ? val.toFixed(2) : '-'
    },
    {
      title: 'Closing Fuel Level (L)',
      dataIndex: 'closingFuelLevel',
      width: 160,
      align: 'right',
      render: (val) => val != null ? val.toFixed(2) : '-'
    },
    {
      title: 'Refuel Detected (L)',
      dataIndex: 'refuelDetected',
      width: 150,
      align: 'right',
      render: (val) => val != null ? val.toFixed(2) : '-'
    },
    {
      title: 'Refuel Entered (L)',
      dataIndex: 'refuelEntered',
      width: 140,
      align: 'right',
      render: (val) => val != null ? val.toFixed(2) : '-'
    },
    {
      title: 'Refuel Difference (L)',
      dataIndex: 'refuelDifference',
      width: 160,
      align: 'right',
      render: (val, record) => {
        if (val == null) return '-';
        return (
          <span style={getCellStyle(val, 'refuelDifference')}>
            {val > 0 ? '+' : ''}{val.toFixed(2)}
          </span>
        );
      },
      sorter: (a, b) => (a.refuelDifference || 0) - (b.refuelDifference || 0)
    },
    {
      title: 'Possible Drain (L)',
      dataIndex: 'possibleDrain',
      width: 140,
      align: 'right',
      render: (val, record) => {
        if (val == null || val <= 0) return '-';
        return (
          <span style={getCellStyle(val, 'possibleDrain')}>
            {val.toFixed(2)}
          </span>
        );
      }
    },
    {
      title: 'Std. Consumption Rate',
      dataIndex: 'stdConsumptionRate',
      width: 180,
      align: 'right',
      render: (val, record) => {
        const unit = getConsumptionRateUnit(record);
        return formatConsumptionRate(val, unit);
      }
    },
    {
      title: 'Consumption Rate',
      dataIndex: 'consumptionRate',
      width: 150,
      align: 'right',
      render: (val, record) => {
        const unit = getConsumptionRateUnit(record);
        return formatConsumptionRate(val, unit);
      }
    },
    {
      title: 'Consumption Rate (Based on Refuel Entered)',
      dataIndex: 'consumptionRateBasedOnRefuelEntered',
      width: 250,
      align: 'right',
      render: (val, record) => {
        if (val == null) return '-';
        const unit = getConsumptionRateUnit(record);
        return formatConsumptionRate(val, unit);
      }
    },
    {
      title: 'Consumption Variance',
      dataIndex: 'consumptionVariance',
      width: 180,
      align: 'right',
      render: (val, record) => {
        if (val == null) return '-';
        return (
          <span style={getCellStyle(val, 'consumptionVariance', record)}>
            {val > 0 ? '+' : ''}{val.toFixed(2)}%
          </span>
        );
      },
      sorter: (a, b) => (a.consumptionVariance || 0) - (b.consumptionVariance || 0)
    },
    {
      title: 'Consumption/Hour (Total)',
      dataIndex: 'consumptionPerHour',
      width: 160,
      align: 'right',
      render: (val) => val != null ? `${val.toFixed(2)} LTR/HOURS` : '-'
    },
    {
      title: 'Consumption/Hour (Moving)',
      dataIndex: 'consumptionPerHourMovingTime',
      width: 180,
      align: 'right',
      render: (val) => val != null ? `${val.toFixed(2)} LTR/HOURS` : '-'
    },
    {
      title: 'Consumption/Hour (Moving, Based on Refuel Entered)',
      dataIndex: 'consumptionPerHourMovingTimeBasedOnRefuelEntered',
      width: 300,
      align: 'right',
      render: (val) => val != null ? `${val.toFixed(2)} LTR/HOURS` : '-'
    },
    {
      title: 'Actions',
      width: 120,
      fixed: 'right',
      render: (text, record) => (
        <Button 
          type="primary" 
          size="small"
          onClick={() => {
            setSelectedAsset(record);
            setAnalysisDrawerVisible(true);
            // Track that analysis has started for this asset
            if (!analysisStarted[record.id]) {
              setAnalysisStarted(prev => ({
                ...prev,
                [record.id]: moment().format('YYYY-MM-DD HH:mm:ss')
              }));
            }
          }}
        >
          {analysisData[record.id] ? 'View/Edit' : 'Analyze'}
        </Button>
      )
    }
  ];

  // Use processed data from CSV
  const reportsData = iotReports;

  // Create a version key for the table to force re-render when analysisData changes
  const tableKey = useMemo(() => {
    // Create a simple hash based on alert statuses in analysisData
    const statusHash = Object.keys(analysisData).map(assetId => {
      const assetAnalysis = analysisData[assetId] || {};
      return Object.keys(assetAnalysis).map(issue => {
        const issueData = assetAnalysis[issue];
        return issueData?.alertStatus || 'none';
      }).join(',');
    }).join('|');
    return `table-${statusHash.length}-${Object.keys(analysisData).length}`;
  }, [analysisData]);

  // Filter and sort data based on priority and alert status
  const filteredAndSortedData = useMemo(() => {
    let data = [...reportsData];

    // Apply priority filter
    if (priorityFilter !== 'all') {
      data = data.filter(item => {
        if (priorityFilter === 'critical') return item.priority === 'critical';
        if (priorityFilter === 'high') return item.priority === 'high';
        if (priorityFilter === 'highConsumption') {
          const unit = getConsumptionRateUnit(item);
          // For KMS/LTR and KWH/L: negative variance is bad (worse fuel efficiency)
          // For LTR/HOURS: positive variance is bad (higher fuel consumption)
          if (unit === 'KMS/LTR' || unit === 'KWH/L') {
            return item.issues.includes('highConsumptionVariance') && 
                   item.consumptionVariance < -10;
          } else if (unit === 'LTR/HOURS') {
            return item.issues.includes('highConsumptionVariance') && 
                   item.consumptionVariance > 10;
          }
          return item.issues.includes('highConsumptionVariance') && 
                 item.consumptionVariance < -10;
        }
        if (priorityFilter === 'highRefuelDiff') {
          return item.issues.includes('highRefuelDifference') && 
                 Math.abs(item.refuelDifference) > 5;
        }
        if (priorityFilter === 'highIdling') {
          return item.issues.includes('highIdling') && 
                 item.idlingPercentage > 5;
        }
        if (priorityFilter === 'dataGap24Plus') {
          return item.issues.includes('dataGap24Plus') && 
                 item.hasDataGap24Plus;
        }
        if (priorityFilter === 'lowDataCompleteness') {
          return item.issues.includes('lowDataCompleteness') && 
                 (item.dataCompleteness || 100) < 95;
        }
        if (priorityFilter === 'greenConsumption') {
          return item.issues.includes('greenConsumptionRate');
        }
        return true;
      });
    }

    // Apply alert status filter (confirmed/discarded)
    if (alertStatusFilter !== 'all') {
      data = data.filter(item => {
        const counts = getAlertStatusCounts(item, analysisData);
        // Check if the asset has at least one alert with the selected alert status
        return counts[alertStatusFilter] > 0;
      });
    }

    // Apply analysis status filter
    if (analysisStatusFilter !== 'all') {
      data = data.filter(item => {
        const counts = getAnalysisStatusCounts(item, analysisData);
        // Check if the asset has at least one alert with the selected analysis status
        return counts[analysisStatusFilter] > 0;
      });
    }

    // Sort by priority order (critical > high > low > normal)
    const priorityOrder = { 'critical': 0, 'high': 1, 'low': 2, 'normal': 3 };
    data.sort((a, b) => {
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Within same priority, sort by consumption variance (most negative first)
      return a.consumptionVariance - b.consumptionVariance;
    });

    return data;
  }, [priorityFilter, alertStatusFilter, analysisStatusFilter, reportsData, analysisData]);

  const criticalCount = reportsData.filter(a => a.priority === 'critical').length;
  const highCount = reportsData.filter(a => a.priority === 'high').length;
  const lowCount = reportsData.filter(a => a.priority === 'low').length;
  // Count analyzed issues across all assets
  const analyzedCount = useMemo(() => {
    let count = 0;
    reportsData.forEach(asset => {
      const assetAnalysis = analysisData[asset.id] || {};
      asset.issues.forEach(issue => {
        if (assetAnalysis[issue] && assetAnalysis[issue].analysis) {
          count++;
        }
      });
    });
    return count;
  }, [analysisData, reportsData]);

  const totalIssuesCount = useMemo(() => {
    return reportsData.reduce((sum, asset) => sum + asset.issues.length, 0);
  }, [reportsData, analysisData]);

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Loading IoT data from CSV files..." />
      </div>
    );
  }

  if (error || iotReports.length === 0) {
    return (
      <div style={{ padding: 24, minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Alert
          message="CSV Files Required"
          description={
            <div>
              <p style={{ marginBottom: 16 }}>
                {error 
                  ? `Failed to load CSV data: ${error}` 
                  : 'No data found. Please ensure CSV files are available.'}
              </p>
              <p style={{ marginBottom: 8, fontWeight: 'bold' }}>Required CSV files:</p>
              <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
                <li><code>public/data/iot-device-report.csv</code></li>
                <li><code>public/data/asset-model-list.csv</code></li>
              </ul>
              <p style={{ marginBottom: 16 }}>
                Please save the CSV files in the <code>public/data/</code> folder and reload the page.
              </p>
              <Button 
                type="primary" 
                onClick={() => window.location.reload()}
                style={{ marginTop: 8 }}
              >
                Reload Page
              </Button>
            </div>
          }
          type="warning"
          showIcon
          style={{ maxWidth: 600 }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <style>{`
        .critical-row {
          background-color: #fff1f0 !important;
        }
        .critical-row:hover {
          background-color: #ffe7e5 !important;
        }
        .high-priority-row {
          background-color: #fff7e6 !important;
        }
        .high-priority-row:hover {
          background-color: #ffecc7 !important;
        }
      `}</style>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic 
              title="Critical Issues" 
              value={criticalCount} 
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="High Priority" 
              value={highCount} 
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Low Priority (Green)" 
              value={lowCount} 
              valueStyle={{ color: '#389e0d' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Issues Analyzed" 
              value={analyzedCount} 
              suffix={`/ ${totalIssuesCount}`}
            />
          </Col>
        </Row>
      </Card>


      <Card>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ marginRight: 8 }}
          />

        <Select
          style={{ width: 250, marginRight: 8 }}
          value={priorityFilter}
          onChange={setPriorityFilter}
          placeholder="Filter by Priority/Issue"
        >
          <Option value="all">All Assets</Option>
          <Option value="critical">Critical Priority</Option>
          <Option value="high">High Priority</Option>
          <Option value="highConsumption">High Consumption Variance (Red)</Option>
          <Option value="highRefuelDiff">High Refuel Difference (Red)</Option>
          <Option value="highIdling">High Idling (Red)</Option>
          <Option value="dataGap24Plus">Data Gap 24+ Hours (Red)</Option>
          <Option value="lowDataCompleteness">Low Data Completeness (&lt; 95%)</Option>
          <Option value="greenConsumption">Green Consumption Rate</Option>
        </Select>

        <Select
          style={{ width: 250, marginRight: 8 }}
          value={alertStatusFilter}
          onChange={setAlertStatusFilter}
          placeholder="Filter by Alert Status"
        >
          <Option value="all">All Alert Status</Option>
          <Option value="confirmed">Confirmed</Option>
          <Option value="discarded">Discarded</Option>
          <Option value="no_status">No Status</Option>
        </Select>

        <Select
          style={{ width: 250 }}
          value={analysisStatusFilter}
          onChange={setAnalysisStatusFilter}
          placeholder="Filter by Analysis Status"
        >
          <Option value="all">All Analysis Statuses</Option>
          <Option value="not_analysed">Not Analysed</Option>
          <Option value="under_analysis">Under Analysis</Option>
          <Option value="analysed">Analysed</Option>
          <Option value="analysed_action_taken">Action Taken</Option>
        </Select>
        </div>

        <Table
          key={tableKey}
          rowKey="id"
          dataSource={filteredAndSortedData}
          columns={columns}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 2500, y: 'calc(100vh - 350px)' }}
          size="small"
          rowClassName={(record) => {
            if (record.priority === 'critical') return 'critical-row';
            if (record.priority === 'high') return 'high-priority-row';
            return '';
          }}
        />
      </Card>

      <AnalysisDrawer
        visible={analysisDrawerVisible}
        asset={selectedAsset}
        analysisData={analysisData[selectedAsset?.id]}
        onClose={() => {
          setAnalysisDrawerVisible(false);
          setSelectedAsset(null);
        }}
        onSave={(data) => {
          try {
            // Save to database (file-based, MongoDB-like structure)
            saveAssetAnalysis(selectedAsset.id, data);
            
            // Update component state
            setAnalysisData(prev => {
              const updated = {
                ...prev,
                [selectedAsset.id]: {
                  ...(prev[selectedAsset.id] || {}),
                  ...data
                }
              };
              return updated;
            });
          } catch (e) {
            console.error('Error saving to database:', e);
            Modal.error({
              title: 'Save Error',
              content: 'Failed to save analysis data. Please try again.'
            });
          }
        }}
      />
    </div>
  );
};

// Analysis Drawer Component - Shows table with each issue as a row
const AnalysisDrawer = ({ visible, asset, analysisData, onClose, onSave }) => {
  const [editingRows, setEditingRows] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [drawerOpenTime, setDrawerOpenTime] = useState(null);
  const [currentUser] = useState('Current User'); // Replace with actual user from auth context
  const [clientMessageModal, setClientMessageModal] = useState({ visible: false, issue: null });
  const [clientMessage, setClientMessage] = useState('');
  const [chartModalVisible, setChartModalVisible] = useState(false);
  const [mainTableDataModalVisible, setMainTableDataModalVisible] = useState(false);

  React.useEffect(() => {
    if (!visible || !asset) {
      setEditingRows({});
      setIsFullScreen(false); // Reset full screen when drawer closes
      setDrawerOpenTime(null);
      return;
    }

    // Track when drawer opens (for overall drawer tracking)
    if (!drawerOpenTime) {
      setDrawerOpenTime(moment());
    }

    // Initialize/update editing state for each issue
    const initialEditing = {};
    const assetAnalysis = analysisData || {};
    const now = moment();
    
    if (asset && asset.issues) {
      asset.issues.forEach(issue => {
        // Merge saved data with current editing data
        const saved = assetAnalysis[issue] || {};
        const current = editingRows[issue] || {};
        // Generate issue ID if it doesn't exist (use asset's data date)
        const issueId = saved.issueId || generateIssueId(asset.id, asset.assetCode, issue, asset.dataUpto);
        
        // Track analysis start time: if issue hasn't been analyzed yet, set start time to now (when drawer opens)
        // If already analyzed, use the existing start time
        // Set start time when drawer opens if it doesn't exist (marks as "Under Analysis")
        const analysisStartTime = saved.analysisStartTime || (saved.analyzedAt ? null : now.format('YYYY-MM-DD HH:mm:ss'));
        
        initialEditing[issue] = {
          issueId: issueId, // Ensure ID is set
          analysis: current.analysis || saved.analysis || '',
          suggestedAction: current.suggestedAction || saved.suggestedAction || '',
          finalAction: current.finalAction || saved.finalAction || '',
          analysisStartTime: analysisStartTime || null,
          // Preserve other time tracking data
          analysisEndTime: saved.analysisEndTime || saved.analyzedAt || null,
          analysisTimeTaken: saved.analysisTimeTaken || saved.analysisDuration || null,
          // Preserve alert status
          alertStatus: saved.alertStatus || null,
          alertStatusUpdatedAt: saved.alertStatusUpdatedAt || null,
          alertStatusUpdatedBy: saved.alertStatusUpdatedBy || null
        };
      });
    }
    setEditingRows(initialEditing);
  }, [visible, asset?.id]);
  
  // Separate effect to update when analysisData changes (after save)
  React.useEffect(() => {
    if (!visible || !asset || !analysisData) return;
    
    // Note: analysisData prop is already asset-specific
    const assetAnalysis = analysisData || {};
    setEditingRows(prev => {
      const updated = { ...prev };
      asset.issues?.forEach(issue => {
        if (assetAnalysis[issue]) {
          updated[issue] = {
            ...updated[issue],
            ...assetAnalysis[issue],
            // Preserve analysisStartTime if it was set in editingRows but not yet saved
            analysisStartTime: assetAnalysis[issue].analysisStartTime || prev[issue]?.analysisStartTime,
            // Preserve alert status
            alertStatus: assetAnalysis[issue].alertStatus || prev[issue]?.alertStatus || null,
            alertStatusUpdatedAt: assetAnalysis[issue].alertStatusUpdatedAt || prev[issue]?.alertStatusUpdatedAt || null,
            alertStatusUpdatedBy: assetAnalysis[issue].alertStatusUpdatedBy || prev[issue]?.alertStatusUpdatedBy || null
          };
        }
      });
      return updated;
    });
  }, [analysisData, visible, asset?.id]);

  const handleFieldChange = (issue, field, value) => {
    setEditingRows(prev => ({
      ...prev,
      [issue]: {
        ...prev[issue],
        [field]: value
      }
    }));
  };

  // Check for unsaved changes by comparing editingRows with analysisData
  // Note: analysisData prop is already asset-specific (analysisData[asset.id])
  const hasUnsavedChanges = () => {
    if (!asset || !asset.issues) return false;
    
    const assetAnalysis = analysisData || {};
    
    for (const issue of asset.issues) {
      const savedData = assetAnalysis[issue] || {};
      const editingData = editingRows[issue] || {};
      
      // Check if any editable fields have changed
      const fieldsToCheck = ['analysis', 'suggestedAction', 'finalAction'];
      
      for (const field of fieldsToCheck) {
        const savedValue = (savedData[field] || '').trim();
        const editingValue = (editingData[field] || '').trim();
        
        // Only consider it unsaved if editing value differs from saved value
        // and editing value is not empty (empty means not entered yet, not unsaved)
        if (editingValue !== savedValue && editingValue !== '') {
          return true; // Found unsaved change
        }
      }
    }
    
    return false;
  };

  // Get list of issues with unsaved changes
  // Note: analysisData prop is already asset-specific (analysisData[asset.id])
  const getUnsavedIssues = () => {
    if (!asset || !asset.issues) return [];
    
    const assetAnalysis = analysisData || {};
    const unsaved = [];
    
    asset.issues.forEach(issue => {
      const savedData = assetAnalysis[issue] || {};
      const editingData = editingRows[issue] || {};
      const unsavedFields = [];
      
      // Check each field - only mark as unsaved if value has changed from saved
      const analysisEditing = (editingData.analysis || '').trim();
      const analysisSaved = (savedData.analysis || '').trim();
      if (analysisEditing !== analysisSaved && analysisEditing !== '') {
        unsavedFields.push('Analysis');
      }
      
      const suggestedActionEditing = (editingData.suggestedAction || '').trim();
      const suggestedActionSaved = (savedData.suggestedAction || '').trim();
      if (suggestedActionEditing !== suggestedActionSaved && suggestedActionEditing !== '') {
        unsavedFields.push('Suggested Action');
      }
      
      const finalActionEditing = (editingData.finalAction || '').trim();
      const finalActionSaved = (savedData.finalAction || '').trim();
      if (finalActionEditing !== finalActionSaved && finalActionEditing !== '') {
        unsavedFields.push('Action Taken');
      }
      
      if (unsavedFields.length > 0) {
        unsaved.push({
          issue,
          issueName: getIssueName(issue),
          fields: unsavedFields
        });
      }
    });
    
    return unsaved;
  };

  const handleSaveRow = (issue, isActionTakenUpdate = false) => {
    const issueData = editingRows[issue];
    const assetAnalysis = analysisData || {};
    const now = moment();
    
    // If updating action taken, only validate that field exists
    if (isActionTakenUpdate) {
      if (!issueData.finalAction) {
        Modal.error({
          title: 'Validation Error',
          content: `Please enter the action taken for ${getIssueName(issue)}`
        });
        return;
      }

      // Calculate response time if issue was sent to client
      let responseTime = null;
      let clientResponseTime = null;
      if (assetAnalysis[issue]?.sentToClientAt) {
        const sentMoment = moment(assetAnalysis[issue].sentToClientAt);
        const duration = moment.duration(now.diff(sentMoment));
        responseTime = {
          seconds: duration.seconds(),
          minutes: duration.minutes(),
          hours: duration.hours(),
          days: Math.floor(duration.asDays()),
          totalSeconds: Math.floor(duration.asSeconds()),
          formatted: formatDuration(duration)
        };
        clientResponseTime = responseTime; // Same value
      }

      // Update only the action taken field, but preserve all existing data
      const existingIssueData = assetAnalysis[issue] || {};
      const dataToSave = {
        [issue]: {
          ...existingIssueData, // Keep existing data including issueId, analysis, suggestedAction
          // Explicitly preserve analysis and suggestedAction if they exist
          analysis: existingIssueData.analysis || issueData.analysis || '',
          suggestedAction: existingIssueData.suggestedAction || issueData.suggestedAction || '',
          finalAction: issueData.finalAction,
          actionTakenUpdatedBy: currentUser,
          actionTakenUpdatedAt: now.format('YYYY-MM-DD HH:mm:ss'),
          responseTime: responseTime,
          clientResponseTime: clientResponseTime,
          updatedAt: now.format('YYYY-MM-DD HH:mm:ss')
        }
      };

      onSave(dataToSave);
      
      // Update editing rows to reflect saved data
      setEditingRows(prev => ({
        ...prev,
        [issue]: {
          ...prev[issue],
          ...dataToSave[issue]
        }
      }));
      
      // Success - no modal needed
      return;
    }

    // For initial save, validate required fields
    if (!issueData.analysis || !issueData.suggestedAction) {
      Modal.error({
        title: 'Validation Error',
        content: `Please fill in Analysis and Suggested Action for ${getIssueName(issue)}`
      });
      return;
    }

    // Get or create issue ID (use asset's data date)
    const issueId = assetAnalysis[issue]?.issueId || generateIssueId(asset.id, asset.assetCode, issue, asset.dataUpto);

    // Get analysis start time - use existing if available, otherwise use current time (first time analyzing)
    const analysisStartTime = assetAnalysis[issue]?.analysisStartTime || issueData.analysisStartTime || now.format('YYYY-MM-DD HH:mm:ss');
    const startMoment = moment(analysisStartTime);
    
    // Calculate analysis duration from start time to now
    let analysisDuration = null;
    let analysisTimeTaken = null;
    if (analysisStartTime) {
      const duration = moment.duration(now.diff(startMoment));
      analysisDuration = {
        seconds: duration.seconds(),
        minutes: duration.minutes(),
        hours: duration.hours(),
        totalSeconds: Math.floor(duration.asSeconds()),
        formatted: formatDuration(duration)
      };
      analysisTimeTaken = analysisDuration;
    }

    // Analysis end time is when analysis is saved (update on each save)
    const analysisEndTime = now.format('YYYY-MM-DD HH:mm:ss');

    // Prepare data to save for this single issue
    const dataToSave = {
      [issue]: {
        issueId: issueId, // Unique identification number
        analysis: issueData.analysis,
        suggestedAction: issueData.suggestedAction,
        finalAction: issueData.finalAction || '', // Optional for initial save
        analyzedBy: currentUser,
        analyzedAt: analysisEndTime, // Update to current time on each save (last edit time)
        analysisStartTime: analysisStartTime, // Preserve original start time
        analysisEndTime: analysisEndTime, // Update end time on each save
        analysisTimeTaken: analysisTimeTaken, // Time taken to analyze
        analysisDuration: analysisDuration, // Keep for backward compatibility
        updatedAt: now.format('YYYY-MM-DD HH:mm:ss'),
        // Preserve action taken update info if it exists
        actionTakenUpdatedBy: assetAnalysis[issue]?.actionTakenUpdatedBy,
        actionTakenUpdatedAt: assetAnalysis[issue]?.actionTakenUpdatedAt,
        // Calculate response time if sent to client and action taken is updated
        responseTime: assetAnalysis[issue]?.responseTime || null,
        clientResponseTime: assetAnalysis[issue]?.clientResponseTime || null,
        // Preserve status if it exists
        status: assetAnalysis[issue]?.status || 'pending',
        sentToClientAt: assetAnalysis[issue]?.sentToClientAt,
        sentToClientBy: assetAnalysis[issue]?.sentToClientBy,
        clientMessage: assetAnalysis[issue]?.clientMessage,
        closedAt: assetAnalysis[issue]?.closedAt,
        closedBy: assetAnalysis[issue]?.closedBy,
        // Preserve alert status if it exists
        alertStatus: assetAnalysis[issue]?.alertStatus || null,
        alertStatusUpdatedAt: assetAnalysis[issue]?.alertStatusUpdatedAt,
        alertStatusUpdatedBy: assetAnalysis[issue]?.alertStatusUpdatedBy
      }
    };

    onSave(dataToSave);
    
    // Update editing rows to reflect saved data (including all time tracking)
    setEditingRows(prev => ({
      ...prev,
      [issue]: {
        ...prev[issue],
        ...dataToSave[issue],
        // Ensure all time tracking fields are preserved
        analysisStartTime: dataToSave[issue].analysisStartTime || prev[issue]?.analysisStartTime,
        analysisEndTime: dataToSave[issue].analysisEndTime,
        analysisTimeTaken: dataToSave[issue].analysisTimeTaken
      }
    }));
    
    // Success - no modal needed
  };

  const formatDuration = (duration) => {
    const days = Math.floor(duration.asDays());
    const hours = Math.floor(duration.asHours()) % 24;
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getActualValueText = (issueKey, asset) => {
    if (!asset) return '';
    
    switch (issueKey) {
      case 'highConsumptionVariance':
      case 'greenConsumptionRate':
        if (asset.consumptionVariance === null || asset.consumptionVariance === undefined) return '';
        const unit = getConsumptionRateUnit(asset);
        return `Variance: ${asset.consumptionVariance > 0 ? '+' : ''}${asset.consumptionVariance.toFixed(2)}% (Std: ${formatConsumptionRate(asset.stdConsumptionRate, unit)}, Actual: ${formatConsumptionRate(asset.consumptionRate, unit)})`;
      
      case 'highRefuelDifference':
        if (asset.refuelDifference === null || asset.refuelDifference === undefined) return '';
        return `Difference: ${asset.refuelDifference > 0 ? '+' : ''}${asset.refuelDifference.toFixed(2)}L (Detected: ${asset.refuelDetected?.toFixed(2)}L, Entered: ${asset.refuelEntered?.toFixed(2)}L)`;
      
      case 'highIdling':
        if (asset.idlingPercentage === null || asset.idlingPercentage === undefined) return '';
        return `Idling: ${asset.idlingPercentage.toFixed(1)}% (Idling Hours: ${asset.idlingHours || '00:00'}, Total Hours: ${asset.runningHours})`;
      
      case 'possibleDrain':
        if (asset.possibleDrain === null || asset.possibleDrain === undefined || asset.possibleDrain <= 0) return '';
        return `Possible Drain: ${asset.possibleDrain.toFixed(2)}L`;
      
      case 'dataGap24Plus':
        if (asset.dataGaps === null || asset.dataGaps === undefined) return '';
        return `Data Gap: ${asset.dataGaps.toFixed(1)} hours (Completeness: ${asset.dataCompleteness?.toFixed(1)}%, Expected: ${asset.expectedDataHours}h, Actual: ${asset.actualDataHours?.toFixed(1)}h)`;
      
      default:
        return '';
    }
  };

  const generateClientMessage = (issue, issueData) => {
    const issueName = getIssueName(issue);
    const actualValueText = getActualValueText(issue, asset);
    const issueId = issueData.issueId || generateIssueId(asset.id, asset.assetCode, issue, asset.dataUpto);
    
    let message = `Dear Client,\n\n`;
    message += `We have identified an issue that requires your attention:\n\n`;
    message += `**Issue ID:** ${issueId}\n\n`;
    message += `**Equipment Details:**\n`;
    message += `- Asset: ${asset.asset}\n`;
    message += `- Model: ${asset.model}\n`;
    message += `- Location: ${asset.location}\n`;
    message += `- Data Period: ${asset.dataFrom ? moment(asset.dataFrom).format('DD-MM-YYYY') : 'N/A'} to ${moment(asset.dataUpto).format('DD-MM-YYYY')}\n\n`;
    
    message += `**Alert Details:**\n`;
    message += `- Issue ID: ${issueId}\n`;
    message += `- Issue Type: ${issueName}\n`;
    message += `- Description: ${getIssueDescription(issue, asset)}\n`;
    if (actualValueText) {
      message += `- Actual Value: ${actualValueText}\n`;
    }
    message += `\n`;
    
    if (issueData.analysis) {
      message += `**Our Analysis:**\n${issueData.analysis}\n\n`;
    }
    
    if (issueData.suggestedAction) {
      message += `**Suggested Action:**\n${issueData.suggestedAction}\n\n`;
    }
    
    message += `Please review and let us know the action you have taken or plan to take.\n\n`;
    message += `Best regards,\nAnalyst Team`;
    
    return message;
  };

  const handleSendToClient = (issue) => {
    // Note: analysisData prop is already asset-specific
    const issueData = analysisData?.[issue] || editingRows[issue] || {};
    
    // Validate that analysis and suggested action exist
    if (!issueData.analysis || !issueData.suggestedAction) {
      Modal.error({
        title: 'Cannot Send to Client',
        content: 'Please save Analysis and Suggested Action before sending to client.'
      });
      return;
    }

    // Generate message
    const message = generateClientMessage(issue, issueData);
    setClientMessage(message);
    setClientMessageModal({ visible: true, issue });
  };

  const handleConfirmSendToClient = () => {
    const { issue } = clientMessageModal;
    const now = moment();
    
    // Update issue status
    const assetAnalysis = analysisData || {};
    const issueData = assetAnalysis[asset.id]?.[issue] || {};
    
    // Ensure issueId exists
    const issueId = issueData.issueId || generateIssueId(asset.id, asset.assetCode, issue, asset.dataUpto);
    
    const dataToSave = {
      [issue]: {
        ...issueData,
        issueId: issueId, // Ensure ID is preserved
        status: 'sent_to_client',
        sentToClientAt: now.format('YYYY-MM-DD HH:mm:ss'),
        sentToClientBy: currentUser,
        clientMessage: clientMessage,
        updatedAt: now.format('YYYY-MM-DD HH:mm:ss')
      }
    };

    onSave(dataToSave);
    
    setClientMessageModal({ visible: false, issue: null });
    setClientMessage('');
    
    Modal.success({
      title: 'Sent to Client',
      content: `Issue "${getIssueName(issue)}" has been sent to the client.`
    });
  };

  const handleCloseIssue = (issue) => {
    Modal.confirm({
      title: 'Close Issue',
      content: `Are you sure you want to close "${getIssueName(issue)}"? This indicates no further action is required.`,
      onOk: () => {
        const now = moment();
        const assetAnalysis = analysisData || {};
        const issueData = assetAnalysis[asset.id]?.[issue] || {};
        
        // Ensure issueId exists
        const issueId = issueData.issueId || generateIssueId(asset.id, asset.assetCode, issue, asset.dataUpto);
        
        const dataToSave = {
          [issue]: {
            ...issueData,
            issueId: issueId, // Ensure ID is preserved
            status: 'closed',
            closedAt: now.format('YYYY-MM-DD HH:mm:ss'),
            closedBy: currentUser,
            updatedAt: now.format('YYYY-MM-DD HH:mm:ss')
          }
        };

        onSave(dataToSave);
        
        Modal.success({
          title: 'Issue Closed',
          content: `Issue "${getIssueName(issue)}" has been closed.`
        });
      }
    });
  };

  const handleMarkAlertStatus = (issue, alertStatus) => {
    const now = moment();
    const assetAnalysis = analysisData || {};
    const savedIssueData = assetAnalysis[asset.id]?.[issue] || {};
    const editingIssueData = editingRows[issue] || {};
    
    // Merge saved data and editing data to preserve all fields, especially analysis and suggestedAction
    const issueData = {
      ...savedIssueData,
      ...editingIssueData,
      // Explicitly preserve critical fields from both sources
      analysis: editingIssueData.analysis || savedIssueData.analysis || '',
      suggestedAction: editingIssueData.suggestedAction || savedIssueData.suggestedAction || '',
      finalAction: editingIssueData.finalAction || savedIssueData.finalAction || '',
      // Preserve all time tracking and other metadata
      analysisStartTime: savedIssueData.analysisStartTime || editingIssueData.analysisStartTime,
      analysisEndTime: savedIssueData.analysisEndTime || editingIssueData.analysisEndTime,
      analysisTimeTaken: savedIssueData.analysisTimeTaken || editingIssueData.analysisTimeTaken,
      analyzedBy: savedIssueData.analyzedBy || editingIssueData.analyzedBy,
      analyzedAt: savedIssueData.analyzedAt || editingIssueData.analyzedAt,
      issueId: savedIssueData.issueId || editingIssueData.issueId || generateIssueId(asset.id, asset.assetCode, issue, asset.dataUpto)
    };
    
    // Ensure issueId exists
    const issueId = issueData.issueId || generateIssueId(asset.id, asset.assetCode, issue, asset.dataUpto);
    
    const dataToSave = {
      [issue]: {
        ...issueData,
        issueId: issueId,
        alertStatus: alertStatus, // 'confirmed', 'discarded', or null
        alertStatusUpdatedAt: alertStatus ? now.format('YYYY-MM-DD HH:mm:ss') : null,
        alertStatusUpdatedBy: alertStatus ? currentUser : null,
        updatedAt: now.format('YYYY-MM-DD HH:mm:ss')
      }
    };

    onSave(dataToSave);
    
    // Update editing rows to reflect the change, preserving all existing data
    setEditingRows(prev => ({
      ...prev,
      [issue]: {
        ...prev[issue],
        ...issueData, // Preserve all existing data including analysis and suggestedAction
        alertStatus: alertStatus,
        alertStatusUpdatedAt: alertStatus ? now.format('YYYY-MM-DD HH:mm:ss') : null,
        alertStatusUpdatedBy: alertStatus ? currentUser : null
      }
    }));
  };

  const getIssueName = (issueKey) => {
    const issueMap = {
      'highConsumptionVariance': 'High Consumption Variance',
      'highRefuelDifference': 'High Refuel Difference',
      'highIdling': 'High Idling',
      'possibleDrain': 'Possible Drain',
      'dataGap24Plus': 'Data Gap 24+ Hours',
      'lowDataCompleteness': 'Low Data Completeness',
      'greenConsumptionRate': 'Abnormal Positive Rate'
    };
    return issueMap[issueKey] || issueKey;
  };

  const generateIssueId = (assetId, assetCode, issueKey, date) => {
    // Format: ASSETCODE-ISSUE-DATE
    // Example: TCO-3905-HCV-20251121
    // Since one asset has only one type of alert per day, we only need the date
    const issueCodeMap = {
      'highConsumptionVariance': 'HCV',
      'highRefuelDifference': 'HRD',
      'highIdling': 'HID',
      'possibleDrain': 'PD',
      'dataGap24Plus': 'DG24',
      'lowDataCompleteness': 'LDC',
      'greenConsumptionRate': 'GCR'
    };
    const issueCode = issueCodeMap[issueKey] || 'ISSUE';
    // Use the asset's data date or current date
    const dateStr = date ? moment(date).format('YYYYMMDD') : moment().format('YYYYMMDD');
    return `${assetCode || assetId}-${issueCode}-${dateStr}`;
  };

  const getOrCreateIssueId = (assetId, assetCode, issueKey, existingData, date) => {
    // Return existing ID if available, otherwise generate new one
    if (existingData?.[assetId]?.[issueKey]?.issueId) {
      return existingData[assetId][issueKey].issueId;
    }
    return generateIssueId(assetId, assetCode, issueKey, date);
  };

  // Mock data for last 7 refueling events with manual vs sensor comparison
  const getLastRefuelingEvents = (asset) => {
    // Mock data - replace with actual API call
    const events = [];
    for (let i = 0; i < 7; i++) {
      const daysAgo = i * 2; // Every 2 days
      const manualQty = 40 + Math.random() * 15; // 40-55L
      const sensorQty = manualQty + (Math.random() * 6 - 3); // ±3L variation
      const difference = sensorQty - manualQty;
      
      events.push({
        date: moment(asset.dataUpto).subtract(daysAgo, 'days').format('YYYY-MM-DD HH:mm'),
        manualQuantity: parseFloat(manualQty.toFixed(2)),
        sensorDetected: parseFloat(sensorQty.toFixed(2)),
        difference: parseFloat(difference.toFixed(2)),
        odometer: 1000 + (daysAgo * 50)
      });
    }
    return events.sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()); // Sort by date descending
  };

  // Mock consumption trend data for last 7 days
  const getConsumptionTrendData = (asset) => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const baseConsumptionRate = asset.consumptionRate || 13.5;
      const baseFuelConsumed = asset.fuelConsumed || 50;
      const baseDistance = asset.distanceTravelled || 100;
      const baseRunningHours = asset.runningHours || '08:00:00';
      const baseIdlingHours = asset.idlingHours || '02:00';
      const baseIdlingPercentage = asset.idlingPercentage || 25;
      
      // Calculate running hours in minutes for calculations
      const runningHoursParts = baseRunningHours.split(':');
      const runningHoursMin = (parseInt(runningHoursParts[0]) || 0) * 60 + (parseInt(runningHoursParts[1]) || 0);
      const idlingHoursParts = baseIdlingHours.split(':');
      const idlingHoursMin = (parseInt(idlingHoursParts[0]) || 0) * 60 + (parseInt(idlingHoursParts[1]) || 0);
      const movingTimeMin = runningHoursMin - idlingHoursMin;
      
      // Add variation
      const variation = Math.random() * 0.3 - 0.15; // ±15% variation
      const fuelConsumed = baseFuelConsumed * (1 + variation);
      const distanceTravelled = baseDistance * (1 + variation);
      const runningHoursMinVar = runningHoursMin * (1 + variation * 0.5);
      const idlingHoursMinVar = idlingHoursMin * (1 + variation * 0.3);
      const movingTimeMinVar = runningHoursMinVar - idlingHoursMinVar;
      
      // Calculate average speed
      const averageSpeed = movingTimeMinVar > 0 && distanceTravelled > 0 
        ? (distanceTravelled * 60) / movingTimeMinVar 
        : null;
      
      // Format times
      const runningHours = formatRunningHours(runningHoursMinVar);
      const idlingHours = formatMovingTime(idlingHoursMinVar);
      const idlingPercentage = runningHoursMinVar > 0 ? (idlingHoursMinVar / runningHoursMinVar) * 100 : 0;
      
      // Calculate consumption rate
      const unit = getConsumptionRateUnit(asset);
      let consumptionRate = baseConsumptionRate * (1 + variation * 0.2);
      if (unit === 'KMS/LTR') {
        consumptionRate = distanceTravelled > 0 && fuelConsumed > 0 ? distanceTravelled / fuelConsumed : consumptionRate;
      } else if (unit === 'LTR/HOURS') {
        const runningHoursDecimal = runningHoursMinVar / 60;
        consumptionRate = runningHoursDecimal > 0 && fuelConsumed > 0 ? fuelConsumed / runningHoursDecimal : consumptionRate;
      }
      
      days.push({
        date: moment(asset.dataUpto).subtract(i, 'days').format('DD-MM-YYYY'),
        consumptionRate: consumptionRate,
        fuelConsumed: parseFloat(fuelConsumed.toFixed(2)),
        distanceTravelled: parseFloat(distanceTravelled.toFixed(2)),
        runningHours: runningHours,
        averageSpeed: averageSpeed ? parseFloat(averageSpeed.toFixed(2)) : null,
        idlingHours: idlingHours,
        idlingPercentage: parseFloat(idlingPercentage.toFixed(1))
      });
    }
    return days;
  };

  // Mock full tank to full tank data
  const getFullTankData = (asset) => {
    const unit = getConsumptionRateUnit(asset);
    const baseFuelConsumed = asset.fuelConsumed || 50;
    const baseDistance = asset.distanceTravelled || 100;
    const baseRunningHours = asset.runningHours || '08:00:00';
    
    // Parse running hours for hours-based calculations
    const runningHoursParts = baseRunningHours.split(':');
    const baseRunningHoursDecimal = (parseInt(runningHoursParts[0]) || 0) + 
                                     (parseInt(runningHoursParts[1]) || 0) / 60 + 
                                     (parseInt(runningHoursParts[2]) || 0) / 3600;
    
    return [
      { 
        event: 1, 
        date: moment(asset.dataUpto).subtract(1, 'days').format('DD-MM-YY'), 
        consumption: 12.5, 
        fuelConsumed: parseFloat((baseFuelConsumed * 0.9 + Math.random() * baseFuelConsumed * 0.2).toFixed(2)),
        distance: unit === 'KMS/LTR' ? parseFloat((baseDistance * 0.9 + Math.random() * baseDistance * 0.2).toFixed(2)) : null,
        hours: unit === 'LTR/HOURS' ? parseFloat((baseRunningHoursDecimal * 0.9 + Math.random() * baseRunningHoursDecimal * 0.2).toFixed(2)) : null,
        kwh: unit === 'KWH/L' ? parseFloat((baseDistance * 0.9 + Math.random() * baseDistance * 0.2).toFixed(2)) : null
      },
      { 
        event: 2, 
        date: moment(asset.dataUpto).subtract(3, 'days').format('DD-MM-YY'), 
        consumption: 13.2, 
        fuelConsumed: parseFloat((baseFuelConsumed * 0.95 + Math.random() * baseFuelConsumed * 0.1).toFixed(2)),
        distance: unit === 'KMS/LTR' ? parseFloat((baseDistance * 0.95 + Math.random() * baseDistance * 0.1).toFixed(2)) : null,
        hours: unit === 'LTR/HOURS' ? parseFloat((baseRunningHoursDecimal * 0.95 + Math.random() * baseRunningHoursDecimal * 0.1).toFixed(2)) : null,
        kwh: unit === 'KWH/L' ? parseFloat((baseDistance * 0.95 + Math.random() * baseDistance * 0.1).toFixed(2)) : null
      },
      { 
        event: 3, 
        date: moment(asset.dataUpto).subtract(5, 'days').format('DD-MM-YY'), 
        consumption: 14.1, 
        fuelConsumed: parseFloat((baseFuelConsumed * 1.0 + Math.random() * baseFuelConsumed * 0.15).toFixed(2)),
        distance: unit === 'KMS/LTR' ? parseFloat((baseDistance * 1.0 + Math.random() * baseDistance * 0.15).toFixed(2)) : null,
        hours: unit === 'LTR/HOURS' ? parseFloat((baseRunningHoursDecimal * 1.0 + Math.random() * baseRunningHoursDecimal * 0.15).toFixed(2)) : null,
        kwh: unit === 'KWH/L' ? parseFloat((baseDistance * 1.0 + Math.random() * baseDistance * 0.15).toFixed(2)) : null
      },
      { 
        event: 4, 
        date: moment(asset.dataUpto).subtract(7, 'days').format('DD-MM-YY'), 
        consumption: 13.8, 
        fuelConsumed: parseFloat((baseFuelConsumed * 0.92 + Math.random() * baseFuelConsumed * 0.16).toFixed(2)),
        distance: unit === 'KMS/LTR' ? parseFloat((baseDistance * 0.92 + Math.random() * baseDistance * 0.16).toFixed(2)) : null,
        hours: unit === 'LTR/HOURS' ? parseFloat((baseRunningHoursDecimal * 0.92 + Math.random() * baseRunningHoursDecimal * 0.16).toFixed(2)) : null,
        kwh: unit === 'KWH/L' ? parseFloat((baseDistance * 0.92 + Math.random() * baseDistance * 0.16).toFixed(2)) : null
      },
      { 
        event: 5, 
        date: moment(asset.dataUpto).subtract(9, 'days').format('DD-MM-YY'), 
        consumption: 12.9, 
        fuelConsumed: parseFloat((baseFuelConsumed * 0.88 + Math.random() * baseFuelConsumed * 0.24).toFixed(2)),
        distance: unit === 'KMS/LTR' ? parseFloat((baseDistance * 0.88 + Math.random() * baseDistance * 0.24).toFixed(2)) : null,
        hours: unit === 'LTR/HOURS' ? parseFloat((baseRunningHoursDecimal * 0.88 + Math.random() * baseRunningHoursDecimal * 0.24).toFixed(2)) : null,
        kwh: unit === 'KWH/L' ? parseFloat((baseDistance * 0.88 + Math.random() * baseDistance * 0.24).toFixed(2)) : null
      },
      { 
        event: 6, 
        date: moment(asset.dataUpto).subtract(11, 'days').format('DD-MM-YY'), 
        consumption: 13.5, 
        fuelConsumed: parseFloat((baseFuelConsumed * 0.93 + Math.random() * baseFuelConsumed * 0.14).toFixed(2)),
        distance: unit === 'KMS/LTR' ? parseFloat((baseDistance * 0.93 + Math.random() * baseDistance * 0.14).toFixed(2)) : null,
        hours: unit === 'LTR/HOURS' ? parseFloat((baseRunningHoursDecimal * 0.93 + Math.random() * baseRunningHoursDecimal * 0.14).toFixed(2)) : null,
        kwh: unit === 'KWH/L' ? parseFloat((baseDistance * 0.93 + Math.random() * baseDistance * 0.14).toFixed(2)) : null
      },
      { 
        event: 7, 
        date: moment(asset.dataUpto).subtract(13, 'days').format('DD-MM-YY'), 
        consumption: 14.3, 
        fuelConsumed: parseFloat((baseFuelConsumed * 1.05 + Math.random() * baseFuelConsumed * 0.1).toFixed(2)),
        distance: unit === 'KMS/LTR' ? parseFloat((baseDistance * 1.05 + Math.random() * baseDistance * 0.1).toFixed(2)) : null,
        hours: unit === 'LTR/HOURS' ? parseFloat((baseRunningHoursDecimal * 1.05 + Math.random() * baseRunningHoursDecimal * 0.1).toFixed(2)) : null,
        kwh: unit === 'KWH/L' ? parseFloat((baseDistance * 1.05 + Math.random() * baseDistance * 0.1).toFixed(2)) : null
      }
    ];
  };

  // Render simple consumption trend chart (using bars)
  const renderConsumptionTrendChart = (asset) => {
    const data = getConsumptionTrendData(asset);
    const maxRate = Math.max(...data.map(d => d.consumptionRate));
    const minRate = Math.min(...data.map(d => d.consumptionRate));
    
    return (
      <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', borderBottom: '2px solid #d9d9d9', paddingBottom: 8 }}>
        {data.map((day, index) => {
          const height = ((day.consumptionRate - minRate) / (maxRate - minRate || 1)) * 100;
          // For KMS/LTR: higher is better (green), for LTR/HOURS and KWH/L: lower is better (green)
          const unit = getConsumptionRateUnit(asset);
          const isHigherBetter = unit === 'KMS/LTR';
          const color = isHigherBetter 
            ? (day.consumptionRate >= asset.stdConsumptionRate ? '#52c41a' : '#ff4d4f')
            : (day.consumptionRate <= asset.stdConsumptionRate ? '#52c41a' : '#ff4d4f');
          return (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div
                style={{
                  width: '80%',
                  height: `${Math.max(height, 10)}%`,
                  backgroundColor: color,
                  borderRadius: '4px 4px 0 0',
                  minHeight: 20,
                  position: 'relative',
                  marginBottom: 4
                }}
                title={`${day.date}: ${day.consumptionRate.toFixed(2)} ${getConsumptionRateUnit(asset)}`}
              >
                <div style={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', fontSize: '10px', whiteSpace: 'nowrap' }}>
                  {day.consumptionRate.toFixed(1)}
                </div>
              </div>
              <div style={{ fontSize: '10px', color: '#666', textAlign: 'center', marginTop: 4 }}>
                {day.date.split('-')[0]}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // AMCharts component for full tank to full tank trend
  const FullTankTrendChart = ({ asset }) => {
    const chartRef = useRef(null);
    const chartDivRef = useRef(null);

    useEffect(() => {
      if (!asset) return;

      const data = getFullTankData(asset);
      if (!data || data.length === 0) return;

      const unit = getConsumptionRateUnit(asset);
      const stdRate = asset.stdConsumptionRate;

      // Create chart instance
      const chart = am4core.create(chartDivRef.current, am4charts.XYChart);
      chartRef.current = chart;

      // Add data
      chart.data = data.map((event, index) => ({
        event: `Event ${event.event}`,
        date: event.date,
        consumption: event.consumption,
        fuelConsumed: event.fuelConsumed || 0,
        eventNumber: event.event,
        index: index
      }));

      // Create category axis (X-axis)
      const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'event';
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.labels.template.rotation = -45;
      categoryAxis.renderer.labels.template.horizontalCenter = 'right';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
      categoryAxis.renderer.labels.template.fontSize = 11;

      // Create value axis (Y-axis) for consumption rate
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.title.text = `Consumption Rate (${unit})`;
      valueAxis.title.fontSize = 12;
      valueAxis.title.fontWeight = 'bold';
      valueAxis.renderer.grid.template.strokeOpacity = 0.1;

      // Create series for consumption rate
      const consumptionSeries = chart.series.push(new am4charts.ColumnSeries());
      consumptionSeries.dataFields.categoryX = 'event';
      consumptionSeries.dataFields.valueY = 'consumption';
      consumptionSeries.name = `Consumption Rate (${unit})`;
      consumptionSeries.columns.template.tooltipText = `Event {eventNumber} ({date}): {valueY.formatNumber('#.00')} ${unit}\nFuel Consumed: {fuelConsumed.formatNumber('#.00')} L`;
      consumptionSeries.columns.template.strokeWidth = 2;
      consumptionSeries.columns.template.strokeOpacity = 1;
      consumptionSeries.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

      // Color coding based on standard rate
      const isHigherBetter = unit === 'KMS/LTR' || unit === 'KWH/L';
      consumptionSeries.columns.template.adapter.add('fill', (fill, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) return fill;
        
        const consumption = dataItem.dataContext.consumption;
        if (stdRate) {
          if (isHigherBetter) {
            return consumption >= stdRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
          } else {
            return consumption <= stdRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
          }
        }
        return am4core.color('#1890ff');
      });

      consumptionSeries.columns.template.adapter.add('stroke', (stroke, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) return stroke;
        
        const consumption = dataItem.dataContext.consumption;
        if (stdRate) {
          if (isHigherBetter) {
            return consumption >= stdRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
          } else {
            return consumption <= stdRate ? am4core.color('#52c41a') : am4core.color('#ff4d4f');
          }
        }
        return am4core.color('#1890ff');
      });

      // Add line series for trend
      const lineSeries = chart.series.push(new am4charts.LineSeries());
      lineSeries.dataFields.categoryX = 'event';
      lineSeries.dataFields.valueY = 'consumption';
      lineSeries.name = 'Trend';
      lineSeries.stroke = am4core.color('#1890ff');
      lineSeries.strokeWidth = 2;
      lineSeries.fill = am4core.color('#1890ff');
      lineSeries.fillOpacity = 0.1;
      lineSeries.tensionX = 0.5;
      lineSeries.bullets.push(new am4charts.CircleBullet());
      lineSeries.bullets.getIndex(0).circle.radius = 4;
      lineSeries.bullets.getIndex(0).circle.fill = am4core.color('#1890ff');
      lineSeries.tooltipText = `Event {eventNumber}: {valueY.formatNumber('#.00')} ${unit}`;

      // Add standard rate line if available
      if (stdRate) {
        const standardRateSeries = chart.series.push(new am4charts.LineSeries());
        standardRateSeries.dataFields.categoryX = 'event';
        standardRateSeries.dataFields.valueY = 'standardRate';
        standardRateSeries.name = 'Standard Rate';
        standardRateSeries.stroke = am4core.color('#faad14');
        standardRateSeries.strokeWidth = 2;
        standardRateSeries.strokeDasharray = '5,5';
        standardRateSeries.data = chart.data.map(item => ({
          ...item,
          standardRate: stdRate
        }));
        standardRateSeries.tooltipText = `Standard Rate: ${stdRate.toFixed(2)} ${unit}`;
      }

      // Add legend
      chart.legend = new am4charts.Legend();
      chart.legend.position = 'top';
      chart.legend.paddingBottom = 20;
      chart.legend.labels.template.maxWidth = 95;

      // Add cursor
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.opacity = 0;

      return () => {
        if (chartRef.current) {
          chartRef.current.dispose();
          chartRef.current = null;
        }
      };
    }, [asset]);

    if (!asset) {
      return <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data available</div>;
    }

    const data = getFullTankData(asset);
    if (!data || data.length === 0) {
      return <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data available</div>;
    }

    return (
      <div 
        ref={chartDivRef} 
        style={{ width: '100%', height: '400px', minHeight: '300px' }}
      />
    );
  };

  // Render full tank to full tank trend chart
  const renderFullTankTrendChart = (asset) => {
    return <FullTankTrendChart asset={asset} />;
  };

  // Get consumption insights
  const getConsumptionInsights = (asset) => {
    const data = getConsumptionTrendData(asset);
    const avgRate = data.reduce((sum, d) => sum + d.consumptionRate, 0) / data.length;
    const stdRate = asset.stdConsumptionRate || 20;
    const variance = ((avgRate - stdRate) / stdRate) * 100;
    
    const unit = getConsumptionRateUnit(asset);
    return [
      `Average consumption rate: ${formatConsumptionRate(avgRate, unit)} (${variance > 0 ? '+' : ''}${variance.toFixed(1)}% vs standard)`,
      `Trend: ${data[data.length - 1].consumptionRate > data[0].consumptionRate ? 'Improving' : 'Declining'} over the period`,
      `Best day: ${data.reduce((max, d) => d.consumptionRate > max.consumptionRate ? d : max, data[0]).date} (${formatConsumptionRate(Math.max(...data.map(d => d.consumptionRate)), getConsumptionRateUnit(asset))})`
    ];
  };

  // Get full tank insights
  const getFullTankInsights = (asset) => {
    const data = getFullTankData(asset);
    const unit = getConsumptionRateUnit(asset);
    const avgConsumption = data.reduce((sum, d) => sum + d.consumption, 0) / data.length;
    const avgFuelConsumed = data.reduce((sum, d) => sum + (d.fuelConsumed || 0), 0) / data.length;
    const stdRate = asset.stdConsumptionRate || 20;
    
    const insights = [
      `Average consumption: ${formatConsumptionRate(avgConsumption, unit)} (${((avgConsumption - stdRate) / stdRate * 100).toFixed(1)}% vs standard)`,
      `Average fuel consumed per tank: ${avgFuelConsumed.toFixed(2)}L`
    ];
    
    if (unit === 'KMS/LTR') {
      const avgDistance = data.reduce((sum, d) => sum + (d.distance || 0), 0) / data.length;
      insights.push(`Average distance per tank: ${avgDistance.toFixed(0)} km`);
    } else if (unit === 'LTR/HOURS') {
      const avgHours = data.reduce((sum, d) => sum + (d.hours || 0), 0) / data.length;
      insights.push(`Average hours per tank: ${avgHours.toFixed(2)} hours`);
    } else if (unit === 'KWH/L') {
      const avgKwh = data.reduce((sum, d) => sum + (d.kwh || 0), 0) / data.length;
      insights.push(`Average KWH per tank: ${avgKwh.toFixed(2)} KWH`);
    }
    
    insights.push(`Consistency: ${data.every(d => Math.abs(d.consumption - avgConsumption) < 1) ? 'Consistent' : 'Variable'} performance across events`);
    
    return insights;
  };

  // Get job cards related to IoT issues for an asset
  const getJobCards = (asset) => {
    // TODO: Replace with actual API call to fetch job cards
    // For now, return empty array - will be populated from backend
    return [];
    // Expected structure:
    // [
    //   {
    //     jobCardId: 'JC-001',
    //     complaint: 'High fuel consumption detected',
    //     status: 'Open',
    //     createdDate: '2025-01-15',
    //     relatedIssue: 'highConsumptionVariance'
    //   }
    // ]
  };

  // Get refueling pattern insights
  const getRefuelingPatternInsights = (asset) => {
    const events = getLastRefuelingEvents(asset);
    const avgDifference = events.reduce((sum, e) => sum + Math.abs(e.difference), 0) / events.length;
    const largeDifferences = events.filter(e => Math.abs(e.difference) > 5).length;
    const consistentPattern = events.slice(0, 5).every(e => Math.abs(e.difference) > 3);
    
    const insights = [];
    
    if (avgDifference > 5) {
      insights.push(`High average difference: ${avgDifference.toFixed(2)}L - Indicates systematic issue`);
    } else if (avgDifference > 2) {
      insights.push(`Moderate average difference: ${avgDifference.toFixed(2)}L - Monitor closely`);
    } else {
      insights.push(`Low average difference: ${avgDifference.toFixed(2)}L - Good alignment`);
    }
    
    if (largeDifferences > 3) {
      insights.push(`${largeDifferences} out of 7 events show large differences (absolute difference >5L) - Pattern detected`);
    } else if (largeDifferences > 0) {
      insights.push(`${largeDifferences} event(s) with large differences - May be isolated incidents`);
    }
    
    if (consistentPattern) {
      insights.push(`Consistent pattern of differences detected - Requires investigation`);
    }
    
    return insights;
  };

  const getIssueDescription = (issueKey, asset = null) => {
    if (issueKey === 'highConsumptionVariance' && asset) {
      const unit = getConsumptionRateUnit(asset);
      if (unit === 'KMS/LTR' || unit === 'KWH/L') {
        return 'Consumption variance is less than -10% (worse fuel efficiency - consuming more fuel than standard)';
      } else if (unit === 'LTR/HOURS') {
        return 'Consumption variance is greater than +10% (higher fuel consumption - consuming more fuel per hour than standard)';
      }
    }
    if (issueKey === 'greenConsumptionRate' && asset) {
      const unit = getConsumptionRateUnit(asset);
      if (unit === 'KMS/LTR' || unit === 'KWH/L') {
        return 'Abnormal positive consumption variance (> 15%) - better fuel efficiency than standard';
      } else if (unit === 'LTR/HOURS') {
        return 'Abnormal negative consumption variance (< -15%) - lower fuel consumption than standard';
      }
    }
    const descMap = {
      'highConsumptionVariance': 'Consumption variance exceeds threshold (worse fuel efficiency)',
      'highRefuelDifference': 'Refuel difference is significant (absolute difference > 5L between detected and entered)',
      'highIdling': 'Idling percentage is greater than 5%',
      'possibleDrain': 'Possible fuel drain detected',
      'dataGap24Plus': 'Data missing for 24 or more hours',
      'lowDataCompleteness': 'Data completeness is less than 95%',
      'greenConsumptionRate': 'Abnormal consumption variance (better than standard)'
    };
    return descMap[issueKey] || '';
  };

  const getActualValueForIssue = (issueKey, asset) => {
    if (!asset) return null;
    
    switch (issueKey) {
      case 'highConsumptionVariance':
      case 'greenConsumptionRate':
        if (asset.consumptionVariance === null || asset.consumptionVariance === undefined) return null;
        const unit = getConsumptionRateUnit(asset);
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              Variance: {asset.consumptionVariance > 0 ? '+' : ''}{asset.consumptionVariance.toFixed(2)}%
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: 2 }}>
              Std: {formatConsumptionRate(asset.stdConsumptionRate, unit)} | 
              Actual: {formatConsumptionRate(asset.consumptionRate, unit)}
            </div>
          </div>
        );
      
      case 'highRefuelDifference':
        if (asset.refuelDifference === null || asset.refuelDifference === undefined) return null;
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              Difference: {asset.refuelDifference > 0 ? '+' : ''}{asset.refuelDifference.toFixed(2)}L
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: 2 }}>
              Detected: {asset.refuelDetected?.toFixed(2)}L | 
              Entered: {asset.refuelEntered?.toFixed(2)}L
            </div>
          </div>
        );
      
      case 'highIdling':
        if (asset.idlingPercentage === null || asset.idlingPercentage === undefined) return null;
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              Idling: {asset.idlingPercentage.toFixed(1)}%
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: 2 }}>
              Idling Hours: {asset.idlingHours || '00:00'} | 
              Total Hours: {asset.runningHours}
            </div>
          </div>
        );
      
      case 'possibleDrain':
        if (asset.possibleDrain === null || asset.possibleDrain === undefined || asset.possibleDrain <= 0) return null;
        return (
          <div style={{ fontWeight: 'bold' }}>
            Possible Drain: {asset.possibleDrain.toFixed(2)}L
          </div>
        );
      
      case 'dataGap24Plus':
        if (asset.dataGaps === null || asset.dataGaps === undefined) return null;
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              Data Gap: {asset.dataGaps.toFixed(1)} hours
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: 2 }}>
              Completeness: {asset.dataCompleteness?.toFixed(1)}% | 
              Expected: {asset.expectedDataHours}h | 
              Actual: {asset.actualDataHours?.toFixed(1)}h
            </div>
          </div>
        );
      
      case 'lowDataCompleteness':
        if (asset.dataCompleteness === null || asset.dataCompleteness === undefined) return null;
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              Data Completeness: {asset.dataCompleteness.toFixed(1)}%
            </div>
            <div style={{ fontSize: '10px', color: '#666', marginTop: 2 }}>
              Expected: {asset.expectedDataHours}h | 
              Actual: {asset.actualDataHours?.toFixed(1)}h | 
              Gap: {asset.dataGaps?.toFixed(1)}h
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!asset) return null;

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12 }}>
          <Button
            type="text"
            onClick={() => setIsFullScreen(!isFullScreen)}
            style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            {isFullScreen ? <Icon type="shrink" /> : <Icon type="arrows-alt" />}
          </Button>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Analysis: {asset.asset}
          </span>
        </div>
      }
      width={isFullScreen ? '100%' : 800}
      visible={visible}
      onClose={() => {
        // First check for unsaved changes
        if (hasUnsavedChanges()) {
          const unsavedIssues = getUnsavedIssues();
          const issueList = unsavedIssues.map(u => `- ${u.issueName}: ${u.fields.join(', ')}`).join('\n');
          
          Modal.confirm({
            title: 'Unsaved Changes',
            width: 600,
            content: (
              <div>
                <Alert
                  message="You have unsaved changes that will be lost if you close the drawer."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Unsaved changes in:</div>
                <div style={{ 
                  backgroundColor: '#fff1f0', 
                  padding: '12px', 
                  borderRadius: 4, 
                  border: '1px solid #ffccc7',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  whiteSpace: 'pre-line'
                }}>
                  {issueList || 'No specific issues found'}
                </div>
                <div style={{ marginTop: 12, color: '#666', fontSize: '12px' }}>
                  Unsaved fields are highlighted in red. Please save your changes before closing.
                </div>
              </div>
            ),
            okText: 'Stay and Save',
            cancelText: 'Close Anyway',
            onOk: () => {
              // Don't close, let user save changes
              return;
            },
            onCancel: () => {
              onClose();
            }
          });
          return;
        }
        
        onClose();
      }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => {
            // First check for unsaved changes
            if (hasUnsavedChanges()) {
              const unsavedIssues = getUnsavedIssues();
              const issueList = unsavedIssues.map(u => `- ${u.issueName}: ${u.fields.join(', ')}`).join('\n');
              
              Modal.confirm({
                title: 'Unsaved Changes',
                width: 600,
                content: (
                  <div>
                    <Alert
                      message="You have unsaved changes that will be lost if you close the drawer."
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Unsaved changes in:</div>
                    <div style={{ 
                      backgroundColor: '#fff1f0', 
                      padding: '12px', 
                      borderRadius: 4, 
                      border: '1px solid #ffccc7',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      whiteSpace: 'pre-line'
                    }}>
                      {issueList || 'No specific issues found'}
                    </div>
                    <div style={{ marginTop: 12, color: '#666', fontSize: '12px' }}>
                      Unsaved fields are highlighted in red. Please save your changes before closing.
                    </div>
                  </div>
                ),
                okText: 'Stay and Save',
                cancelText: 'Close Anyway',
                onOk: () => {
                  // Don't close, let user save changes
                  return;
                },
                onCancel: () => {
                  onClose();
                }
              });
              return;
            }
            
            onClose();
          }}>Close</Button>
        </div>
      }
    >
      <div>
        <Card title="Asset Information" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <p>
                <strong>Asset Code:</strong>{' '}
                {asset.assetCode ? (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      Modal.info({
                        title: 'Asset Report',
                        content: 'This link will open the Asset Report in a new window. Feature coming soon.',
                        okText: 'OK'
                      });
                    }}
                    style={{ color: '#1890ff', textDecoration: 'underline' }}
                  >
                    {asset.assetCode}
                  </a>
                ) : (
                  'N/A'
                )}
              </p>
              <p><strong>Asset:</strong> {asset.asset}</p>
              <p><strong>Model:</strong> {asset.model}</p>
              <p><strong>Location:</strong> {asset.location}</p>
              <p><strong>Vehicle Age:</strong> {calculateVehicleAge(asset.purchaseDate)}</p>
              {asset.purchaseDate && (
                <p style={{ fontSize: '12px', color: '#666' }}>
                  <strong>Purchase Date:</strong> {moment(asset.purchaseDate).format('DD-MM-YYYY')}
                </p>
              )}
            </Col>
            <Col span={12}>
              <p><strong>Priority:</strong> {getPriorityBadge(asset)}</p>
              <p><strong>Issues:</strong> {getIssueTags(asset)}</p>
              <p><strong>Data From:</strong> {asset.dataFrom ? moment(asset.dataFrom).format('DD-MM-YYYY HH:mm') : 'N/A'}</p>
              <p><strong>Data Upto:</strong> {moment(asset.dataUpto).format('DD-MM-YYYY HH:mm')}</p>
              <p style={{ marginTop: 12 }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    Modal.info({
                      title: 'Calibration Chart',
                      content: 'This link will open the Calibration Chart for this asset in a new window. Feature coming soon.',
                      okText: 'OK'
                    });
                  }}
                  style={{ color: '#1890ff', textDecoration: 'underline' }}
                >
                  <Icon type="line-chart" style={{ marginRight: 4 }} />
                  View Calibration Chart
                </a>
              </p>
              <p>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    Modal.info({
                      title: 'Idle Settings',
                      content: 'This link will open the Idle Settings for this asset in a new window. Feature coming soon.',
                      okText: 'OK'
                    });
                  }}
                  style={{ color: '#1890ff', textDecoration: 'underline' }}
                >
                  <Icon type="setting" style={{ marginRight: 4 }} />
                  Configure Idle Settings
                </a>
              </p>
              {asset.hasDataGap24Plus && (
                <p style={{ color: '#cf1322', fontWeight: 'bold' }}>
                  ⚠️ Data Gap: {asset.dataGaps?.toFixed(1)} hours missing (24+ hours)
                </p>
              )}
            </Col>
          </Row>
        </Card>

        {/* Analysis Data Section */}
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Analysis Data</span>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <Button
                  type="link"
                  icon={<Icon type="table" />}
                  onClick={() => setMainTableDataModalVisible(true)}
                  style={{ padding: 0 }}
                >
                  View Day Summary
                </Button>
                <Button
                  type="link"
                  icon={<Icon type="bar-chart" />}
                  onClick={() => {
                    const periodStart = asset.dataFrom ? moment(asset.dataFrom).format('DD-MM-YYYY HH:mm') : 'N/A';
                    const periodEnd = asset.dataUpto ? moment(asset.dataUpto).format('DD-MM-YYYY HH:mm') : 'N/A';
                    Modal.info({
                      title: 'View Charts',
                      content: (
                        <div>
                          <p>This will open charts relevant to the current analysis period in a new window.</p>
                          <p style={{ marginTop: 12, fontSize: '12px', color: '#666' }}>
                            <strong>Analysis Period:</strong><br />
                            From: {periodStart}<br />
                            To: {periodEnd}
                          </p>
                          <p style={{ marginTop: 12, fontSize: '12px', color: '#999' }}>
                            Feature coming soon.
                          </p>
                        </div>
                      ),
                      okText: 'OK',
                      width: 500
                    });
                  }}
                  style={{ padding: 0 }}
                >
                  View Charts
                </Button>
              </div>
            </div>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Collapse ghost>
            {/* Last 7 Refueling Events */}
            <Panel 
              header={(() => {
                const refuelingEvents = getLastRefuelingEvents(asset);
                const avgDifference = refuelingEvents.length > 0 
                  ? refuelingEvents.reduce((sum, e) => sum + Math.abs(e.difference), 0) / refuelingEvents.length 
                  : 0;
                const avgDiffColor = avgDifference > 5 ? '#cf1322' : avgDifference > 2 ? '#faad14' : '#52c41a';
                return (
                  <span>
                    Last 7 Refueling Events (Manual vs Sensor)
                    <span style={{ marginLeft: 12, color: avgDiffColor, fontWeight: 'bold' }}>
                      Avg Difference: {avgDifference.toFixed(2)}L
                    </span>
                  </span>
                );
              })()}
              key="refueling"
            >
              <div style={{ padding: '8px 0' }}>
                {(() => {
                  const refuelingEvents = getLastRefuelingEvents(asset);
                  return (
                    <Table
                      dataSource={refuelingEvents}
                      columns={[
                        {
                          title: 'Date',
                          dataIndex: 'date',
                          width: 150,
                          render: (date) => moment(date).format('DD-MM-YYYY HH:mm')
                        },
                        {
                          title: 'Manual Entry (L)',
                          dataIndex: 'manualQuantity',
                          width: 140,
                          align: 'right',
                          render: (val) => val.toFixed(2)
                        },
                        {
                          title: 'Sensor Detected (L)',
                          dataIndex: 'sensorDetected',
                          width: 150,
                          align: 'right',
                          render: (val) => val.toFixed(2)
                        },
                        {
                          title: 'Difference (L)',
                          dataIndex: 'difference',
                          width: 130,
                          align: 'right',
                          render: (diff, record) => {
                            const absDiff = Math.abs(diff);
                            const color = absDiff > 5 ? '#cf1322' : absDiff > 2 ? '#faad14' : '#52c41a';
                            return (
                              <span style={{ color, fontWeight: 'bold' }}>
                                {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                              </span>
                            );
                          }
                        },
                        {
                          title: 'Odometer (km)',
                          dataIndex: 'odometer',
                          width: 120,
                          align: 'right',
                          render: (val) => val.toFixed(0)
                        },
                        {
                          title: 'Pattern',
                          width: 120,
                          render: (text, record, index) => {
                            // Check if this is part of a pattern using the refuelingEvents array
                            const recentDiffs = refuelingEvents.slice(0, index + 1).map(r => Math.abs(r.difference));
                            const avgRecentDiff = recentDiffs.reduce((a, b) => a + b, 0) / recentDiffs.length;
                            
                            if (avgRecentDiff > 5 && index >= 2) {
                              return <Tag color="red">Consistent Difference</Tag>;
                            } else if (avgRecentDiff > 2 && index >= 2) {
                              return <Tag color="orange">Occasional Difference</Tag>;
                            } else if (Math.abs(record.difference) > 5) {
                              return <Tag color="red">One-off Large</Tag>;
                            } else {
                              return <Tag color="green">Normal</Tag>;
                            }
                          }
                        }
                      ]}
                      pagination={false}
                      size="small"
                      rowKey={(record, index) => `refuel-${index}`}
                    />
                  );
                })()}
                <div style={{ marginTop: 12, fontSize: '12px', color: '#666' }}>
                  <strong>Pattern Analysis:</strong>
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    {getRefuelingPatternInsights(asset).map((insight, idx) => (
                      <li key={idx}>{insight}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Panel>

            {/* Consumption Trends */}
            <Panel 
              header={(() => {
                const trendData = getConsumptionTrendData(asset);
                const avgConsumptionRate = trendData.length > 0 
                  ? trendData.reduce((sum, d) => sum + d.consumptionRate, 0) / trendData.length 
                  : 0;
                const unit = getConsumptionRateUnit(asset);
                const stdRate = asset.stdConsumptionRate;
                let avgDiffColor = '#52c41a'; // Default green
                if (stdRate) {
                  const variance = ((avgConsumptionRate - stdRate) / stdRate) * 100;
                  if (unit === 'KMS/LTR' || unit === 'KWH/L') {
                    // For KMS/LTR and KWH/L: negative variance is bad (worse efficiency)
                    avgDiffColor = variance < -10 ? '#cf1322' : variance > 10 ? '#52c41a' : '#faad14';
                  } else if (unit === 'LTR/HOURS') {
                    // For LTR/HOURS: positive variance is bad (higher consumption)
                    avgDiffColor = variance > 10 ? '#cf1322' : variance < -10 ? '#52c41a' : '#faad14';
                  }
                }
                return (
                  <span>
                    Consumption Rate Trend (Last 7 Days)
                    <span style={{ marginLeft: 12, color: avgDiffColor, fontWeight: 'bold' }}>
                      Avg: {formatConsumptionRate(avgConsumptionRate, unit)}
                    </span>
                  </span>
                );
              })()}
              key="consumption"
            >
              <div style={{ padding: '16px 0' }}>
                {renderConsumptionTrendChart(asset)}
                <div style={{ marginTop: 16 }}>
                  <Table
                    dataSource={getConsumptionTrendData(asset)}
                    columns={[
                      { title: 'Date', dataIndex: 'date', width: 100, fixed: 'left' },
                      { 
                        title: `Consumption Rate (${getConsumptionRateUnit(asset)})`, 
                        dataIndex: 'consumptionRate', 
                        width: 150,
                        align: 'right',
                        render: (val) => val != null ? formatConsumptionRate(val, getConsumptionRateUnit(asset)) : '-'
                      },
                      { 
                        title: 'Fuel Consumed (L)', 
                        dataIndex: 'fuelConsumed', 
                        width: 130,
                        align: 'right',
                        render: (val) => val != null ? val.toFixed(2) : '-'
                      },
                      { 
                        title: 'Distance (km)', 
                        dataIndex: 'distanceTravelled', 
                        width: 110,
                        align: 'right',
                        render: (val) => val != null ? val.toFixed(2) : '-'
                      },
                      { 
                        title: 'Running Hours', 
                        dataIndex: 'runningHours', 
                        width: 120,
                        align: 'right'
                      },
                      { 
                        title: 'Average Speed (KM/H)', 
                        dataIndex: 'averageSpeed', 
                        width: 140,
                        align: 'right',
                        render: (val) => val != null ? val.toFixed(2) : '-'
                      },
                      { 
                        title: 'Idling Hours', 
                        dataIndex: 'idlingHours', 
                        width: 110,
                        align: 'right'
                      },
                      { 
                        title: 'Idling %', 
                        dataIndex: 'idlingPercentage', 
                        width: 90,
                        align: 'right',
                        render: (val) => val != null ? `${val.toFixed(1)}%` : '-'
                      }
                    ]}
                    pagination={false}
                    size="small"
                    rowKey="date"
                    scroll={{ x: 900 }}
                  />
                </div>
                <div style={{ marginTop: 12, fontSize: '12px', color: '#666' }}>
                  <strong>Key Insights:</strong>
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    {getConsumptionInsights(asset).map((insight, idx) => (
                      <li key={idx}>{insight}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Panel>
            <Panel 
              header={(() => {
                const fullTankData = getFullTankData(asset);
                const avgConsumptionRate = fullTankData.length > 0 
                  ? fullTankData.reduce((sum, d) => sum + d.consumption, 0) / fullTankData.length 
                  : 0;
                const unit = getConsumptionRateUnit(asset);
                const stdRate = asset.stdConsumptionRate;
                let avgDiffColor = '#52c41a'; // Default green
                if (stdRate) {
                  const variance = ((avgConsumptionRate - stdRate) / stdRate) * 100;
                  if (unit === 'KMS/LTR' || unit === 'KWH/L') {
                    // For KMS/LTR and KWH/L: negative variance is bad (worse efficiency)
                    avgDiffColor = variance < -10 ? '#cf1322' : variance > 10 ? '#52c41a' : '#faad14';
                  } else if (unit === 'LTR/HOURS') {
                    // For LTR/HOURS: positive variance is bad (higher consumption)
                    avgDiffColor = variance > 10 ? '#cf1322' : variance < -10 ? '#52c41a' : '#faad14';
                  }
                }
                return (
                  <span>
                    Full Tank to Full Tank Consumption (Last 7 Events)
                    <span style={{ marginLeft: 12, color: avgDiffColor, fontWeight: 'bold' }}>
                      Avg: {formatConsumptionRate(avgConsumptionRate, unit)}
                    </span>
                  </span>
                );
              })()}
              key="fulltank"
            >
              <div style={{ padding: '16px 0' }}>
                {renderFullTankTrendChart(asset)}
                <div style={{ marginTop: 16 }}>
                  <Table
                    dataSource={getFullTankData(asset)}
                    columns={[
                      { title: 'Event', dataIndex: 'event', width: 80 },
                      { title: 'Date', dataIndex: 'date', width: 100 },
                      { title: `Consumption Rate (${getConsumptionRateUnit(asset)})`, dataIndex: 'consumption', width: 150, align: 'right', render: (val) => formatConsumptionRate(val, getConsumptionRateUnit(asset)) },
                      { title: 'Fuel Consumed (L)', dataIndex: 'fuelConsumed', width: 130, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' },
                      ...(getConsumptionRateUnit(asset) === 'KMS/LTR' ? [
                        { title: 'Distance (km)', dataIndex: 'distance', width: 120, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' }
                      ] : []),
                      ...(getConsumptionRateUnit(asset) === 'LTR/HOURS' ? [
                        { title: 'Hours Run', dataIndex: 'hours', width: 120, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' }
                      ] : []),
                      ...(getConsumptionRateUnit(asset) === 'KWH/L' ? [
                        { title: 'KWH Run', dataIndex: 'kwh', width: 120, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' }
                      ] : [])
                    ]}
                    pagination={false}
                    size="small"
                    rowKey="event"
                  />
                </div>
                <div style={{ marginTop: 12, fontSize: '12px', color: '#666' }}>
                  <strong>Key Insights:</strong>
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    {getFullTankInsights(asset).map((insight, idx) => (
                      <li key={idx}>{insight}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Panel>

            {/* Open Job Cards */}
            <Panel 
              key="jobcards"
              header={(() => {
                const jobCards = getJobCards(asset);
                const openCount = jobCards.filter(jc => 
                  jc.status === 'Open' || 
                  jc.status === 'In Progress' || 
                  jc.status === 'Pending'
                ).length;
                return (
                  <span>
                    Open Job Cards Related to IoT Issues - {openCount}
                  </span>
                );
              })()}
            >
              <div style={{ padding: '16px 0' }}>
                {(() => {
                  const jobCards = getJobCards(asset);
                  const openJobCards = jobCards.filter(jc => 
                    jc.status === 'Open' || 
                    jc.status === 'In Progress' || 
                    jc.status === 'Pending'
                  );
                  
                  if (openJobCards.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        <Icon type="file-text" style={{ fontSize: 24, marginBottom: 8 }} />
                        <p>No open job cards related to IoT issues</p>
                      </div>
                    );
                  }

                  return (
                    <Table
                      dataSource={openJobCards}
                      columns={[
                        {
                          title: 'Job Card ID',
                          dataIndex: 'jobCardId',
                          width: 120,
                          render: (text) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                Modal.info({
                                  title: 'Job Card Details',
                                  content: `This will open Job Card ${text} in a new window. Feature coming soon.`,
                                  okText: 'OK'
                                });
                              }}
                              style={{ color: '#1890ff' }}
                            >
                              {text}
                            </a>
                          )
                        },
                        {
                          title: 'Complaint',
                          dataIndex: 'complaint',
                          ellipsis: true,
                          render: (text) => (
                            <Tooltip title={text}>
                              <span>{text || 'N/A'}</span>
                            </Tooltip>
                          )
                        },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          width: 120,
                          render: (status) => {
                            let color = 'default';
                            if (status === 'Open') color = 'blue';
                            else if (status === 'In Progress') color = 'orange';
                            else if (status === 'Pending') color = 'yellow';
                            else if (status === 'Resolved') color = 'green';
                            else if (status === 'Closed') color = 'default';
                            
                            return <Tag color={color}>{status || 'N/A'}</Tag>;
                          }
                        },
                        {
                          title: 'Created Date',
                          dataIndex: 'createdDate',
                          width: 120,
                          render: (date) => date ? moment(date).format('DD-MM-YYYY') : 'N/A'
                        },
                        {
                          title: 'Related Issue',
                          dataIndex: 'relatedIssue',
                          width: 150,
                          render: (issue) => {
                            const issueLabels = {
                              highConsumptionVariance: 'High Consumption',
                              highRefuelDifference: 'Refuel Difference',
                              highIdling: 'High Idling',
                              lowDataCompleteness: 'Low Data Completeness'
                            };
                            return issueLabels[issue] || issue || 'N/A';
                          }
                        }
                      ]}
                      pagination={false}
                      size="small"
                      rowKey="jobCardId"
                    />
                  );
                })()}
              </div>
            </Panel>
          </Collapse>
        </Card>

        <Divider>Issue-by-Issue Analysis</Divider>

        {asset.issues && asset.issues.length > 0 ? (
          <Table
            rowKey={(record) => `${asset.id}-${record.issue}`}
            dataSource={asset.issues.map(issue => ({
              issue,
              assetId: asset.id,
              assetName: asset.asset,
              asset: asset // Pass full asset object to access values
            }))}
            columns={[
              {
                title: 'Alert/Issue',
                width: 300,
                fixed: 'left',
                render: (text, record) => {
                  // Note: analysisData prop is already asset-specific
                  const issueData = analysisData?.[record.issue] || {};
                  const editingData = editingRows[record.issue] || {};
                  // Merge saved and editing data for status determination
                  const mergedData = {
                    ...issueData,
                    ...editingData,
                    analysis: editingData.analysis || issueData.analysis,
                    suggestedAction: editingData.suggestedAction || issueData.suggestedAction,
                    finalAction: editingData.finalAction || issueData.finalAction,
                    alertStatus: editingData.alertStatus || issueData.alertStatus
                  };
                  const isAnalyzed = mergedData?.analysis && mergedData?.suggestedAction;
                  const status = issueData?.status || 'pending';
                  const alertStatus = mergedData?.alertStatus || null;
                  const hasAnalysisStartTime = issueData?.analysisStartTime || editingData?.analysisStartTime;
                  const actualValue = getActualValueForIssue(record.issue, record.asset);
                  // Get or generate issue ID (use asset's data date)
                  const issueId = issueData?.issueId || generateIssueId(asset.id, asset.assetCode, record.issue, asset.dataUpto);
                  
                  // Determine the current alert status
                  const currentAlertStatus = getAlertStatus(mergedData, hasAnalysisStartTime);
                  
                  return (
                    <div>
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: 'bold',
                        color: '#1890ff',
                        marginBottom: 8,
                        padding: '4px 8px',
                        backgroundColor: '#e6f7ff',
                        borderRadius: 4,
                        border: '1px solid #91d5ff',
                        display: 'inline-block'
                      }}>
                        ID: {issueId}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                        <Tag color={record.issue === 'greenConsumptionRate' ? 'green' : 'red'}>
                          {getIssueName(record.issue)}
                        </Tag>
                        {currentAlertStatus === 'not_analysed' && (
                          <Badge status="default" text="Not Analysed" style={{ marginLeft: 4 }} />
                        )}
                        {currentAlertStatus === 'under_analysis' && (
                          <Badge status="processing" text="Under Analysis" style={{ marginLeft: 4 }} />
                        )}
                        {currentAlertStatus === 'analysed' && (
                          <Badge status="success" text="Analysed" style={{ marginLeft: 4 }} />
                        )}
                        {currentAlertStatus === 'analysed_action_taken' && (
                          <Badge status="warning" text="Action Taken" style={{ marginLeft: 4 }} />
                        )}
                        {currentAlertStatus === 'analysed_action_taken_confirmed' && (
                          <Badge status="success" text="Confirmed" style={{ marginLeft: 4 }} />
                        )}
                        {currentAlertStatus === 'analysed_action_taken_discarded' && (
                          <Badge status="error" text="Discarded" style={{ marginLeft: 4 }} />
                        )}
                        {status === 'sent_to_client' && (
                          <Badge status="processing" text="Sent" style={{ marginLeft: 4 }} />
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: 4 }}>
                        {getIssueDescription(record.issue, asset)}
                      </div>
                      {actualValue && (
                        <div style={{ 
                          fontSize: '11px',
                          color: record.issue === 'greenConsumptionRate' ? '#389e0d' : '#cf1322',
                          marginTop: 6,
                          padding: '6px 8px',
                          backgroundColor: record.issue === 'greenConsumptionRate' ? '#f6ffed' : '#fff1f0',
                          borderRadius: 4,
                          border: `1px solid ${record.issue === 'greenConsumptionRate' ? '#b7eb8f' : '#ffccc7'}`
                        }}>
                          {actualValue}
                        </div>
                      )}
                    </div>
                  );
                }
              },
              {
                title: 'Analysis',
                width: 300,
                render: (text, record) => {
                  const issueData = editingRows[record.issue] || {};
                  const savedData = analysisData?.[record.issue] || {};
                  // Only show red border if the value has been changed from saved value
                  const editingValue = (issueData.analysis || '').trim();
                  const savedValue = (savedData.analysis || '').trim();
                  const hasUnsaved = editingValue !== savedValue && editingValue !== '';
                  
                  return (
                    <TextArea
                      rows={3}
                      value={issueData.analysis || ''}
                      onChange={(e) => handleFieldChange(record.issue, 'analysis', e.target.value)}
                      placeholder="Enter analysis for this issue..."
                      style={{ 
                        width: '100%',
                        borderColor: hasUnsaved ? '#ff4d4f' : undefined,
                        borderWidth: hasUnsaved ? '2px' : undefined
                      }}
                    />
                  );
                }
              },
              {
                title: 'Suggested Action',
                width: 300,
                render: (text, record) => {
                  const issueData = editingRows[record.issue] || {};
                  const savedData = analysisData?.[record.issue] || {};
                  // Only show red border if the value has been changed from saved value
                  const editingValue = (issueData.suggestedAction || '').trim();
                  const savedValue = (savedData.suggestedAction || '').trim();
                  const hasUnsaved = editingValue !== savedValue && editingValue !== '';
                  
                  return (
                    <TextArea
                      rows={3}
                      value={issueData.suggestedAction || ''}
                      onChange={(e) => handleFieldChange(record.issue, 'suggestedAction', e.target.value)}
                      placeholder="Enter suggested action..."
                      style={{ 
                        width: '100%',
                        borderColor: hasUnsaved ? '#ff4d4f' : undefined,
                        borderWidth: hasUnsaved ? '2px' : undefined
                      }}
                    />
                  );
                }
              },
              {
                title: 'Action Taken (Optional - Update Later)',
                width: 300,
                render: (text, record) => {
                  const issueData = editingRows[record.issue] || {};
                  const savedData = analysisData?.[record.issue] || {};
                  // Only show red border if the value has been changed from saved value
                  const editingValue = (issueData.finalAction || '').trim();
                  const savedValue = (savedData.finalAction || '').trim();
                  const hasUnsaved = editingValue !== savedValue && editingValue !== '';
                  
                  return (
                    <div>
                      <TextArea
                        rows={3}
                        value={issueData.finalAction || ''}
                        onChange={(e) => handleFieldChange(record.issue, 'finalAction', e.target.value)}
                        placeholder="Enter action taken by client (can be updated later)..."
                        style={{ 
                          width: '100%',
                          borderColor: hasUnsaved ? '#ff4d4f' : undefined,
                          borderWidth: hasUnsaved ? '2px' : undefined
                        }}
                      />
                      {savedData?.actionTakenUpdatedBy && (
                        <div style={{ fontSize: '10px', color: '#1890ff', marginTop: 4 }}>
                          Last updated by: {savedData.actionTakenUpdatedBy} on {moment(savedData.actionTakenUpdatedAt).format('DD-MM-YYYY HH:mm')}
                        </div>
                      )}
                    </div>
                  );
                }
              },
              {
                title: 'Time Tracking',
                width: 200,
                render: (text, record) => {
                  // Note: analysisData prop is already asset-specific
                  const issueData = analysisData?.[record.issue];
                  const editingData = editingRows[record.issue] || {};
                  
                  // Get time tracking data - prioritize saved data, fallback to editing data
                  const analysisStartTime = issueData?.analysisStartTime || editingData?.analysisStartTime;
                  const analysisEndTime = issueData?.analysisEndTime || issueData?.analyzedAt || editingData?.analysisEndTime;
                  const timeTaken = issueData?.analysisTimeTaken || issueData?.analysisDuration || editingData?.analysisTimeTaken || editingData?.analysisDuration;
                  const responseTime = issueData?.responseTime || issueData?.clientResponseTime || editingData?.responseTime;
                  const sentToClientAt = issueData?.sentToClientAt || editingData?.sentToClientAt;
                  const actionTakenUpdatedAt = issueData?.actionTakenUpdatedAt || editingData?.actionTakenUpdatedAt;
                  
                  // Format time taken if it exists but doesn't have formatted property
                  let timeTakenFormatted = null;
                  if (timeTaken) {
                    if (timeTaken.formatted) {
                      timeTakenFormatted = timeTaken.formatted;
                    } else if (timeTaken.totalSeconds !== undefined) {
                      const duration = moment.duration(timeTaken.totalSeconds, 'seconds');
                      timeTakenFormatted = formatDuration(duration);
                    } else if (analysisStartTime && analysisEndTime) {
                      // Calculate from start and end times
                      try {
                        const startMoment = moment(analysisStartTime);
                        const endMoment = moment(analysisEndTime);
                        if (startMoment.isValid() && endMoment.isValid()) {
                          const duration = moment.duration(endMoment.diff(startMoment));
                          timeTakenFormatted = formatDuration(duration);
                        }
                      } catch (e) {
                        console.error('Error calculating time taken:', e);
                      }
                    }
                  }
                  
                  // Format response time
                  let responseTimeFormatted = null;
                  if (responseTime) {
                    if (responseTime.formatted) {
                      responseTimeFormatted = responseTime.formatted;
                    } else if (responseTime.totalSeconds !== undefined) {
                      const duration = moment.duration(responseTime.totalSeconds, 'seconds');
                      responseTimeFormatted = formatDuration(duration);
                    }
                  }
                  
                  return (
                    <div style={{ fontSize: '10px', lineHeight: '1.6' }}>
                      {analysisStartTime && (
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ fontWeight: 'bold', color: '#666' }}>Start:</div>
                          <div style={{ color: '#999' }}>{moment(analysisStartTime).format('DD-MM-YY HH:mm')}</div>
                        </div>
                      )}
                      {analysisEndTime && (
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ fontWeight: 'bold', color: '#666' }}>End:</div>
                          <div style={{ color: '#999' }}>{moment(analysisEndTime).format('DD-MM-YY HH:mm')}</div>
                        </div>
                      )}
                      {timeTakenFormatted && (
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ fontWeight: 'bold', color: '#666' }}>Time Taken:</div>
                          <div style={{ color: '#52c41a' }}>{timeTakenFormatted}</div>
                        </div>
                      )}
                      {actionTakenUpdatedAt && (
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ fontWeight: 'bold', color: '#666' }}>Action Updated:</div>
                          <div style={{ color: '#52c41a' }}>{moment(actionTakenUpdatedAt).format('DD-MM-YY HH:mm')}</div>
                        </div>
                      )}
                      {sentToClientAt && (
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ fontWeight: 'bold', color: '#666' }}>Sent:</div>
                          <div style={{ color: '#1890ff' }}>{moment(sentToClientAt).format('DD-MM-YY HH:mm')}</div>
                        </div>
                      )}
                      {responseTimeFormatted && (
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ fontWeight: 'bold', color: '#666' }}>Response Time:</div>
                          <div style={{ color: '#fa8c16' }}>{responseTimeFormatted}</div>
                        </div>
                      )}
                      {!analysisStartTime && !analysisEndTime && (
                        <div style={{ color: '#999', fontStyle: 'italic' }}>Not started</div>
                      )}
                    </div>
                  );
                }
              },
              {
                title: 'Status & Actions',
                width: 280,
                fixed: 'right',
                render: (text, record) => {
                  // Note: analysisData prop is already asset-specific
                  const issueData = analysisData?.[record.issue] || {};
                  const editingData = editingRows[record.issue] || {};
                  // Merge saved and editing data for status determination
                  const mergedData = {
                    ...issueData,
                    ...editingData,
                    analysis: editingData.analysis || issueData.analysis,
                    suggestedAction: editingData.suggestedAction || issueData.suggestedAction,
                    finalAction: editingData.finalAction || issueData.finalAction,
                    alertStatus: editingData.alertStatus || issueData.alertStatus
                  };
                  // Check both saved data and current editing data
                  const hasAnalysis = (issueData?.analysis && issueData?.suggestedAction) || 
                                     (editingData?.analysis && editingData?.suggestedAction);
                  const hasActionTaken = issueData?.finalAction || editingData?.finalAction;
                  const status = issueData?.status || 'pending';
                  const alertStatus = issueData?.alertStatus || editingData?.alertStatus || null;
                  const hasAnalysisStartTime = issueData?.analysisStartTime || editingData?.analysisStartTime;
                  
                  // Determine the current alert status
                  const currentAlertStatus = getAlertStatus(mergedData, hasAnalysisStartTime);
                  
                  return (
                    <div>
                      {/* Status Badge */}
                      <div style={{ marginBottom: 8 }}>
                        {currentAlertStatus === 'not_analysed' && (
                          <Badge status="default" text="Not Analysed" />
                        )}
                        {currentAlertStatus === 'under_analysis' && (
                          <Badge status="processing" text="Under Analysis" />
                        )}
                        {currentAlertStatus === 'analysed' && (
                          <Badge status="success" text="Analysed" />
                        )}
                        {currentAlertStatus === 'analysed_action_taken' && (
                          <Badge status="warning" text="Analysed & Action Taken" />
                        )}
                        {currentAlertStatus === 'analysed_action_taken_confirmed' && (
                          <Badge status="success" text="Analysed, Action Taken & Confirmed" />
                        )}
                        {currentAlertStatus === 'analysed_action_taken_discarded' && (
                          <Badge status="error" text="Analysed, Action Taken & Discarded" />
                        )}
                        {status === 'sent_to_client' && (
                          <Badge status="processing" text="Sent to Client" style={{ marginLeft: 4 }} />
                        )}
                      </div>

                      {/* Save Buttons */}
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleSaveRow(record.issue, false)}
                        style={{ marginBottom: 4, width: '100%' }}
                        disabled={!editingRows[record.issue]?.analysis || !editingRows[record.issue]?.suggestedAction}
                      >
                        {hasAnalysis ? 'Update Analysis' : 'Save Analysis'}
                      </Button>
                      <Button
                        type="default"
                        size="small"
                        onClick={() => handleSaveRow(record.issue, true)}
                        style={{ marginBottom: 4, width: '100%' }}
                        disabled={!editingRows[record.issue]?.finalAction}
                      >
                        {hasActionTaken ? 'Update Action Taken' : 'Save Action Taken'}
                      </Button>

                      {/* Alert Status Buttons - Show for all analyzed alerts, allow changing status */}
                      {hasAnalysis && (
                        <div style={{ marginBottom: 8, padding: '8px', backgroundColor: '#fff7e6', borderRadius: 4, border: '1px solid #ffd591' }}>
                          <div style={{ fontSize: '10px', color: '#666', marginBottom: 4, fontWeight: 'bold' }}>
                            Alert Status:
                          </div>
                          <Button
                            type={alertStatus === 'confirmed' ? 'primary' : 'default'}
                            size="small"
                            onClick={() => handleMarkAlertStatus(record.issue, 'confirmed')}
                            style={{ 
                              marginRight: 4, 
                              width: '48%', 
                              backgroundColor: alertStatus === 'confirmed' ? '#52c41a' : undefined,
                              borderColor: alertStatus === 'confirmed' ? '#52c41a' : undefined,
                              color: alertStatus === 'confirmed' ? '#fff' : undefined
                            }}
                          >
                            {alertStatus === 'confirmed' ? '✓ Confirmed' : 'Confirm'}
                          </Button>
                          <Button
                            type={alertStatus === 'discarded' ? 'danger' : 'default'}
                            size="small"
                            onClick={() => handleMarkAlertStatus(record.issue, 'discarded')}
                            style={{ 
                              width: '48%',
                              backgroundColor: alertStatus === 'discarded' ? '#ff4d4f' : undefined,
                              borderColor: alertStatus === 'discarded' ? '#ff4d4f' : undefined,
                              color: alertStatus === 'discarded' ? '#fff' : undefined
                            }}
                          >
                            {alertStatus === 'discarded' ? '✓ Discarded' : 'Discard'}
                          </Button>
                          {alertStatus && (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleMarkAlertStatus(record.issue, null)}
                              style={{ 
                                width: '100%', 
                                marginTop: 4, 
                                padding: 0,
                                height: 'auto',
                                fontSize: '10px'
                              }}
                            >
                              Clear Status
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Action Buttons - Only show if analysis is saved */}
                      {hasAnalysis && status !== 'closed' && (
                        <Button
                          type="default"
                          size="small"
                          onClick={() => handleSendToClient(record.issue)}
                          style={{ marginBottom: 4, width: '100%', borderColor: '#1890ff', color: '#1890ff' }}
                          disabled={status === 'sent_to_client'}
                        >
                          {status === 'sent_to_client' ? 'Sent to Client' : 'Send to Client'}
                        </Button>
                      )}

                      {/* Metadata - Simplified since we have Time Tracking column */}
                      {issueData?.analyzedBy && (
                        <div style={{ fontSize: '9px', color: '#999', marginTop: 4 }}>
                          <div>By: {issueData.analyzedBy || 'N/A'}</div>
                        </div>
                      )}
                    </div>
                  );
                }
              }
            ]}
            pagination={false}
            size="small"
            scroll={{ x: 1200 }}
          />
        ) : (
          <Alert
            message="No Issues"
            description="This asset has no issues requiring analysis."
            type="success"
          />
        )}
      </div>

      {/* Charts Modal */}
      <Modal
        title={`Charts & Analysis: ${asset.asset}`}
        visible={chartModalVisible}
        onCancel={() => setChartModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setChartModalVisible(false)}>Close</Button>
        ]}
        width={1000}
      >
        <div>
          <Card 
            title={(() => {
              const trendData = getConsumptionTrendData(asset);
              const avgConsumptionRate = trendData.length > 0 
                ? trendData.reduce((sum, d) => sum + d.consumptionRate, 0) / trendData.length 
                : 0;
              const unit = getConsumptionRateUnit(asset);
              const stdRate = asset.stdConsumptionRate;
              let avgDiffColor = '#52c41a'; // Default green
              if (stdRate) {
                const variance = ((avgConsumptionRate - stdRate) / stdRate) * 100;
                if (unit === 'KMS/LTR' || unit === 'KWH/L') {
                  // For KMS/LTR and KWH/L: negative variance is bad (worse efficiency)
                  avgDiffColor = variance < -10 ? '#cf1322' : variance > 10 ? '#52c41a' : '#faad14';
                } else if (unit === 'LTR/HOURS') {
                  // For LTR/HOURS: positive variance is bad (higher consumption)
                  avgDiffColor = variance > 10 ? '#cf1322' : variance < -10 ? '#52c41a' : '#faad14';
                }
              }
              return (
                <span>
                  Consumption Rate Trend (Last 7 Days)
                  <span style={{ marginLeft: 12, color: avgDiffColor, fontWeight: 'bold' }}>
                    Avg: {formatConsumptionRate(avgConsumptionRate, unit)}
                  </span>
                </span>
              );
            })()}
            style={{ marginBottom: 16 }}
          >
            {renderConsumptionTrendChart(asset)}
            <div style={{ marginTop: 16 }}>
              <Table
                dataSource={getConsumptionTrendData(asset)}
                columns={[
                  { title: 'Date', dataIndex: 'date', width: 100, fixed: 'left' },
                  { 
                    title: `Consumption Rate (${getConsumptionRateUnit(asset)})`, 
                    dataIndex: 'consumptionRate', 
                    width: 150,
                    align: 'right',
                    render: (val) => val != null ? formatConsumptionRate(val, getConsumptionRateUnit(asset)) : '-'
                  },
                  { 
                    title: 'Fuel Consumed (L)', 
                    dataIndex: 'fuelConsumed', 
                    width: 130,
                    align: 'right',
                    render: (val) => val != null ? val.toFixed(2) : '-'
                  },
                  { 
                    title: 'Distance (km)', 
                    dataIndex: 'distanceTravelled', 
                    width: 110,
                    align: 'right',
                    render: (val) => val != null ? val.toFixed(2) : '-'
                  },
                  { 
                    title: 'Running Hours', 
                    dataIndex: 'runningHours', 
                    width: 120,
                    align: 'right'
                  },
                  { 
                    title: 'Average Speed (KM/H)', 
                    dataIndex: 'averageSpeed', 
                    width: 140,
                    align: 'right',
                    render: (val) => val != null ? val.toFixed(2) : '-'
                  },
                  { 
                    title: 'Idling Hours', 
                    dataIndex: 'idlingHours', 
                    width: 110,
                    align: 'right'
                  },
                  { 
                    title: 'Idling %', 
                    dataIndex: 'idlingPercentage', 
                    width: 90,
                    align: 'right',
                    render: (val) => val != null ? `${val.toFixed(1)}%` : '-'
                  }
                ]}
                pagination={false}
                size="small"
                rowKey="date"
                scroll={{ x: 900 }}
              />
            </div>
          </Card>

          <Card 
            title={(() => {
              const fullTankData = getFullTankData(asset);
              const avgConsumptionRate = fullTankData.length > 0 
                ? fullTankData.reduce((sum, d) => sum + d.consumption, 0) / fullTankData.length 
                : 0;
              const unit = getConsumptionRateUnit(asset);
              const stdRate = asset.stdConsumptionRate;
              let avgDiffColor = '#52c41a'; // Default green
              if (stdRate) {
                const variance = ((avgConsumptionRate - stdRate) / stdRate) * 100;
                if (unit === 'KMS/LTR' || unit === 'KWH/L') {
                  // For KMS/LTR and KWH/L: negative variance is bad (worse efficiency)
                  avgDiffColor = variance < -10 ? '#cf1322' : variance > 10 ? '#52c41a' : '#faad14';
                } else if (unit === 'LTR/HOURS') {
                  // For LTR/HOURS: positive variance is bad (higher consumption)
                  avgDiffColor = variance > 10 ? '#cf1322' : variance < -10 ? '#52c41a' : '#faad14';
                }
              }
              return (
                <span>
                  Full Tank to Full Tank Consumption (Last 7 Events)
                  <span style={{ marginLeft: 12, color: avgDiffColor, fontWeight: 'bold' }}>
                    Avg: {formatConsumptionRate(avgConsumptionRate, unit)}
                  </span>
                </span>
              );
            })()}
          >
            {renderFullTankTrendChart(asset)}
            <div style={{ marginTop: 16 }}>
              <Table
                dataSource={getFullTankData(asset)}
                columns={[
                  { title: 'Event', dataIndex: 'event', width: 80 },
                  { title: 'Date', dataIndex: 'date', width: 100 },
                  { title: `Consumption Rate (${getConsumptionRateUnit(asset)})`, dataIndex: 'consumption', width: 150, align: 'right', render: (val) => formatConsumptionRate(val, getConsumptionRateUnit(asset)) },
                  { title: 'Fuel Consumed (L)', dataIndex: 'fuelConsumed', width: 130, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' },
                  ...(getConsumptionRateUnit(asset) === 'KMS/LTR' ? [
                    { title: 'Distance (km)', dataIndex: 'distance', width: 120, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' }
                  ] : []),
                  ...(getConsumptionRateUnit(asset) === 'LTR/HOURS' ? [
                    { title: 'Hours Run', dataIndex: 'hours', width: 120, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' }
                  ] : []),
                  ...(getConsumptionRateUnit(asset) === 'KWH/L' ? [
                    { title: 'KWH Run', dataIndex: 'kwh', width: 120, align: 'right', render: (val) => val != null ? val.toFixed(2) : '-' }
                  ] : [])
                ]}
                pagination={false}
                size="small"
                rowKey="event"
              />
            </div>
          </Card>
        </div>
      </Modal>

      {/* Main Table Data Modal */}
      <Modal
        title={`View Day Summary: ${asset.asset}`}
        visible={mainTableDataModalVisible}
        onCancel={() => setMainTableDataModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setMainTableDataModalVisible(false)}>Close</Button>
        ]}
        width={1200}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Table
            dataSource={[asset]}
            columns={[
              {
                title: 'Priority',
                width: 120,
                render: () => getPriorityBadge(asset)
              },
              {
                title: 'Asset',
                width: 200,
                render: () => (
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{asset.asset}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>{asset.model}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>{asset.location}</div>
                  </div>
                )
              },
              {
                title: 'Data Upto',
                width: 160,
                render: () => moment(asset.dataUpto).format('DD-MM-YYYY HH:mm')
              },
              {
                title: 'Data Completeness',
                width: 180,
                render: () => {
                  const completeness = asset.dataCompleteness || 100;
                  const hasGap = asset.hasDataGap24Plus;
                  const gapHours = asset.dataGaps || 0;
                  const isLowCompleteness = completeness < 95;
                  
                  return (
                    <div>
                      <div style={(hasGap || isLowCompleteness) ? { color: '#cf1322', fontWeight: 'bold' } : {}}>
                        {completeness.toFixed(1)}%
                      </div>
                      {hasGap && (
                        <div style={{ fontSize: '11px', color: '#cf1322' }}>
                          Gap: {gapHours.toFixed(1)}h
                        </div>
                      )}
                      {hasGap && (
                        <Tag color="red" style={{ marginTop: 4 }}>24+ Hours Missing</Tag>
                      )}
                      {isLowCompleteness && !hasGap && (
                        <Tag color="red" style={{ marginTop: 4 }}>Low Completeness</Tag>
                      )}
                    </div>
                  );
                }
              },
              {
                title: 'Running Hours',
                dataIndex: 'runningHours',
                width: 130,
                align: 'right'
              },
              {
                title: 'Moving Time',
                dataIndex: 'movingTime',
                width: 120,
                align: 'right',
                render: (val) => val || '-'
              },
              {
                title: 'Idling Hours',
                dataIndex: 'idlingHours',
                width: 120,
                align: 'right',
                render: (val) => val || '-'
              },
              {
                title: 'Idling %',
                dataIndex: 'idlingPercentage',
                width: 100,
                align: 'right',
                render: (val) => {
                  if (val == null) return '-';
                  const isRed = val > 5;
                  return (
                    <span style={isRed ? { color: '#cf1322', fontWeight: 'bold' } : {}}>
                      {val.toFixed(1)}%
                    </span>
                  );
                }
              },
              {
                title: 'Average Speed (KM/H)',
                dataIndex: 'averageSpeed',
                width: 150,
                align: 'right',
                render: (val) => val != null ? val.toFixed(2) : '-'
              },
              {
                title: 'Distance (kms)',
                dataIndex: 'distanceTravelled',
                width: 120,
                align: 'right',
                render: (val) => val != null ? val.toFixed(2) : '-'
              },
              {
                title: 'Fuel Consumed (L)',
                dataIndex: 'fuelConsumed',
                width: 140,
                align: 'right',
                render: (val) => val != null ? val.toFixed(2) : '-'
              },
              {
                title: 'Opening Fuel Level (L)',
                dataIndex: 'openingFuelLevel',
                width: 160,
                align: 'right',
                render: (val) => val != null ? val.toFixed(2) : '-'
              },
              {
                title: 'Closing Fuel Level (L)',
                dataIndex: 'closingFuelLevel',
                width: 160,
                align: 'right',
                render: (val) => val != null ? val.toFixed(2) : '-'
              },
              {
                title: 'Refuel Detected (L)',
                dataIndex: 'refuelDetected',
                width: 150,
                align: 'right',
                render: (val) => val != null ? val.toFixed(2) : '-'
              },
              {
                title: 'Refuel Entered (L)',
                dataIndex: 'refuelEntered',
                width: 140,
                align: 'right',
                render: (val) => val != null ? val.toFixed(2) : '-'
              },
              {
                title: 'Refuel Difference (L)',
                dataIndex: 'refuelDifference',
                width: 160,
                align: 'right',
                render: (val) => {
                  if (val == null) return '-';
                  const isRed = Math.abs(val) > 5;
                  return (
                    <span style={isRed ? { color: '#cf1322', fontWeight: 'bold' } : {}}>
                      {val > 0 ? '+' : ''}{val.toFixed(2)}
                    </span>
                  );
                }
              },
              {
                title: 'Possible Drain (L)',
                dataIndex: 'possibleDrain',
                width: 140,
                align: 'right',
                render: (val) => {
                  if (val == null || val <= 0) return '-';
                  return (
                    <span style={{ color: '#cf1322', fontWeight: 'bold' }}>
                      {val.toFixed(2)}
                    </span>
                  );
                }
              },
              {
                title: 'Std. Consumption Rate',
                dataIndex: 'stdConsumptionRate',
                width: 180,
                align: 'right',
                render: () => {
                  const unit = getConsumptionRateUnit(asset);
                  return formatConsumptionRate(asset.stdConsumptionRate, unit);
                }
              },
              {
                title: 'Consumption Rate',
                dataIndex: 'consumptionRate',
                width: 150,
                align: 'right',
                render: () => {
                  const unit = getConsumptionRateUnit(asset);
                  return formatConsumptionRate(asset.consumptionRate, unit);
                }
              },
              {
                title: 'Consumption Rate (Based on Refuel Entered)',
                dataIndex: 'consumptionRateBasedOnRefuelEntered',
                width: 250,
                align: 'right',
                render: () => {
                  if (asset.consumptionRateBasedOnRefuelEntered == null) return '-';
                  const unit = getConsumptionRateUnit(asset);
                  return formatConsumptionRate(asset.consumptionRateBasedOnRefuelEntered, unit);
                }
              },
              {
                title: 'Consumption Variance',
                dataIndex: 'consumptionVariance',
                width: 180,
                align: 'right',
                render: () => {
                  if (asset.consumptionVariance == null) return '-';
                  const unit = getConsumptionRateUnit(asset);
                  const isRed = (unit === 'KMS/LTR' || unit === 'KWH/L') 
                    ? asset.consumptionVariance < -10 
                    : asset.consumptionVariance > 10;
                  return (
                    <span style={isRed ? { color: '#cf1322', fontWeight: 'bold' } : {}}>
                      {asset.consumptionVariance > 0 ? '+' : ''}{asset.consumptionVariance.toFixed(2)}%
                    </span>
                  );
                }
              },
              {
                title: 'Consumption/Hour (Total)',
                dataIndex: 'consumptionPerHour',
                width: 160,
                align: 'right',
                render: (val) => val != null ? `${val.toFixed(2)} LTR/HOURS` : '-'
              },
              {
                title: 'Consumption/Hour (Moving)',
                dataIndex: 'consumptionPerHourMovingTime',
                width: 180,
                align: 'right',
                render: (val) => val != null ? `${val.toFixed(2)} LTR/HOURS` : '-'
              },
              {
                title: 'Consumption/Hour (Moving, Based on Refuel Entered)',
                dataIndex: 'consumptionPerHourMovingTimeBasedOnRefuelEntered',
                width: 300,
                align: 'right',
                render: (val) => val != null ? `${val.toFixed(2)} LTR/HOURS` : '-'
              },
              {
                title: 'Issues & Alert Status',
                width: 350,
                render: () => {
                  const issuesWithStatus = getIssueTagsWithStatus(asset, { [asset.id]: analysisData });
                  const counts = getAlertStatusCounts(asset, { [asset.id]: analysisData });
                  
                  return (
                    <div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                        {issuesWithStatus}
                      </div>
                      {asset.issues && asset.issues.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#666', borderTop: '1px solid #f0f0f0', paddingTop: 4 }}>
                          <span style={{ color: '#52c41a' }}>✓ Confirmed: {counts.confirmed}</span>
                          {' | '}
                          <span style={{ color: '#ff4d4f' }}>✗ Discarded: {counts.discarded}</span>
                          {' | '}
                          <span style={{ color: '#999' }}>○ No Status: {counts.no_status}</span>
                        </div>
                      )}
                    </div>
                  );
                }
              },
              {
                title: 'Analysis Status',
                width: 300,
                render: () => {
                  const counts = getAnalysisStatusCounts(asset, { [asset.id]: analysisData });
                  
                  return (
                    <div style={{ fontSize: '12px' }}>
                      {counts.not_analysed > 0 && (
                        <div style={{ marginBottom: 4 }}>
                          <Badge status="default" text={`Not Analysed: ${counts.not_analysed}`} />
                        </div>
                      )}
                      {counts.under_analysis > 0 && (
                        <div style={{ marginBottom: 4 }}>
                          <Badge status="processing" text={`Under Analysis: ${counts.under_analysis}`} />
                        </div>
                      )}
                      {counts.analysed > 0 && (
                        <div style={{ marginBottom: 4 }}>
                          <Badge status="success" text={`Analysed: ${counts.analysed}`} />
                        </div>
                      )}
                      {counts.analysed_action_taken > 0 && (
                        <div style={{ marginBottom: 4 }}>
                          <Badge status="warning" text={`Action Taken: ${counts.analysed_action_taken}`} />
                        </div>
                      )}
                    </div>
                  );
                }
              }
            ]}
            pagination={false}
            size="small"
            rowKey="id"
            scroll={{ x: 2000 }}
          />
        </div>
      </Modal>

      {/* Client Message Modal */}
      <Modal
        title={`Send to Client: ${clientMessageModal.issue ? getIssueName(clientMessageModal.issue) : ''}`}
        visible={clientMessageModal.visible}
        onCancel={() => {
          setClientMessageModal({ visible: false, issue: null });
          setClientMessage('');
        }}
        onOk={handleConfirmSendToClient}
        okText="Send to Client"
        width={800}
      >
        <Alert
          message="Auto-generated Message"
          description="The message below has been auto-generated with equipment and issue details. You can edit it before sending."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <TextArea
          rows={20}
          value={clientMessage}
          onChange={(e) => setClientMessage(e.target.value)}
          placeholder="Client message will be generated here..."
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        />
        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <strong>Note:</strong> This message will be sent to the client. Make sure all information is accurate before sending.
        </div>
      </Modal>
    </Drawer>
  );
};


export default IoTDailyReportAnalysis;

